import { fetchWithChatFeedback } from './chatFeedbackRequired';
/**
 * GPC (Government Purchase Card) Workflow API Functions
 *
 * Mirrors `servicesFunctions.ts`: one function per `/api/gpc/*` endpoint,
 * following the repo error contract — `error_code`/`technical_detail` are
 * copied onto thrown `Error` objects for analytics consumers, while UI
 * rendering routes the message through `toUserFacingError()`. Raw
 * `technical_detail` never reaches JSX.
 *
 * Backend stage numbers (see `backend/flow_storage.py`):
 *   800 questions · 801 salient characteristics (MFR) · 802 product + price
 *   discovery + threshold determination. Completion is at 802
 *   (`GPC_MAX_STAGES = 3` this iteration).
 *
 * Phase 8 ([NOTES-3], 2026-06-19): Stage 1 reverts to ITEM-ONLY questions;
 * Stage 3 runs the search → buy-type → quantity → threshold flow. The
 * deferred Phase-7 pieces (eCARS approval stage 803, the Path-B decide
 * hierarchy, the manual ≥3-quote grid) are NOT in the active flow — their
 * client functions are retained but unused.
 */

import { apiCall, type ApiResponse } from './apiClient';
import { USER_FACING_ERROR_MESSAGES } from '../utils/userFacingError';
import { composeGpcNextSteps } from '../utils/gpcNextSteps';
import { VerifyFlag } from '../utils/gpcVerifyFlags';
import { Question, MFR, Product } from '../types/pipeline';
import { mapOverspecDecisions } from '../utils/mfrProjection';

/**
 * A GPC product/quote returned by the (optionally source-scoped) product search.
 * GPC reuses the shared pipeline `Product` shape; this alias names it per the
 * GPC contract so the mandatory-source stage props read naturally.
 */
export type GpcProduct = Product;

/**
 * Structured GPC buy parameters. **Phase 8 ([NOTES-3]):** these are no longer
 * Stage-1 form inputs — they are derived / produced in Stage 3:
 *   - `unit_price` comes from the open-market price SEARCH.
 *   - `quantity` is entered by the user in Stage 3.
 *   - `dollar_amount` is DERIVED = `unit_price × quantity` (the threshold total).
 *   - `buy_type` is AGENT-determined and surfaced (editable); `buy_type_agent_source`
 *     is `true` while agent-produced, flipped `false` once the user edits it.
 *
 * Mirrors the backend `GpcBuyParams` model (`farchat/models/gpc_data.py`).
 */
export interface GpcBuyParams {
  requirement_title?: string;
  unit_price?: number | null;
  quantity?: number | null;
  dollar_amount?: number | null;
  buy_type?: 'supply' | 'service' | 'construction' | null;
  buy_type_agent_source?: boolean;
}

/** In-scope GPC dollar bands (mirrors backend `ThresholdBand`). */
export type GpcThresholdBand = 'at_or_below_mpt' | 'over_mpt_to_25k';

/**
 * The two sourcing paths selected by the dollar band (2026-06-19 refactor).
 * Path A (≤MPT) = open-market-first + ≥3-quote capture; Path B (>MPT) =
 * decide-first mandatory-source ordered hierarchy + eCARS sign-off. Mirrors the
 * backend `SourcingPath` literal.
 */
export type GpcSourcingPath = 'open_market' | 'mandatory_source';

/**
 * Output of the deterministic decide engine — mirrors the backend
 * `GpcSourceDecision` model (`farchat/models/gpc_data.py`).
 *
 * Phase 7 (2026-06-19): `sourcing_path` discriminates Path A (open-market) from
 * Path B (mandatory-source); `source_hierarchy` is Path B's ordered fallback
 * list (supply_system → af_advantage → gsa_advantage); `requires_889_verification`
 * is set on Path B.
 */
export interface GpcSourceDecision {
  sourcing_path: GpcSourcingPath | null;
  category: string;
  recommended_source: string;
  source_hierarchy: string[];
  order_of_precedence: string[];
  threshold_band: GpcThresholdBand | null;
  obligations: string[];
  requires_dd2579: boolean;
  requires_889_verification: boolean;
  requires_ecars: boolean;
  // UNION: new flows carry structured flags (mapped to copy via
  // composeVerifyFlag); OLD persisted flows carry legacy plain-sentence strings.
  verify_flags: Array<VerifyFlag | string>;
  user_confirmed: boolean;
  user_override_source: string | null;
  /**
   * Multi-select mandatory-source picks (2026-06-22): the AVAILABLE (wired)
   * sources the cardholder ticked via checkboxes. When non-empty this is the
   * union the product search is scoped to; gated/non-wireable sources are never
   * selectable. Empty = fall back to recommended/override scoping.
   */
  selected_sources: string[];
}

/**
 * Vendor contact affordances for a "contact for pricing" (outcome B) quote —
 * mirrors the backend `GpcVendorContact` model. Extracted from the product page
 * (or the vendor domain `/contact` fallback) so the cardholder can request the
 * quote. Every field is optional; an empty/absent object means "no contact
 * affordance could be found" (the instruction text still renders).
 */
export interface GpcVendorContact {
  phone?: string | null;
  email?: string | null;
  quote_url?: string | null;
  contact_url?: string | null;
}

/**
 * The three pricing outcomes per candidate (mirrors the backend `price_status`):
 *   - `'found'`      — a verified price (a GSA structured catalog price OR a
 *                      price that literally appears on the page). Renders
 *                      unchanged.
 *   - `'on_request'` — no price on the page; surface `vendor_contact` + the
 *                      "Price on request — contact <vendor>" instruction and a
 *                      quote-entry field. `price` is null.
 *   - `'not_found'`  — no price and no contact (rare in the surfaced set).
 */
export type GpcPriceStatus = 'found' | 'on_request' | 'not_found';

/**
 * A single Path-A (open-market) price quote — mirrors the backend
 * `GpcPriceQuote` model. Path A documents price reasonableness by capturing ≥3
 * quotes; a small disadvantaged business may be chosen even if NOT cheapest and
 * vendors need not be on SAM.gov (neither flag constrains validity).
 */
export interface GpcPriceQuote {
  source: string;
  vendor_name: string;
  price: number | null;
  url: string | null;
  is_small_disadvantaged_business: boolean;
  on_sam_gov: boolean | null;
  /**
   * Phase 8 ([NOTES-3]): the Path-A price history is now POPULATED FROM THE
   * AUTO-SEARCH, not user entry. `from_search` defaults `true` (a search
   * result). Mirrors the backend `GpcPriceQuote.from_search`.
   */
  from_search?: boolean;
  captured_at: string | null;
  /**
   * Per-quote acceptability verdict + one-sentence rationale from the
   * price-extraction agent (whether this source/listing is a good price match
   * for the requirement item). `null`/`undefined` when not assessed. Mirrors the
   * backend `GpcPriceQuote.acceptable` / `.rationale`.
   */
  acceptable?: boolean | null;
  rationale?: string | null;
  /**
   * Contact-for-pricing fields (2026-06-26). `price_status` classifies the quote
   * into one of the three pricing outcomes (defaults `'found'` so existing
   * priced quotes type-check unchanged); `vendor_contact` carries the contact
   * affordances for an `'on_request'` quote (null/undefined when not applicable).
   */
  price_status?: GpcPriceStatus;
  vendor_contact?: GpcVendorContact | null;
  /**
   * `true` whenever there is no verified price for this quote — the user must
   * obtain the price (e.g. via the contact-for-pricing path) and enter it
   * manually. Mirrors the backend `GpcPriceQuote.requires_manual_price`.
   */
  requires_manual_price?: boolean;
}

/** In-scope GPC threshold band (mirrors backend `ThresholdBand`), camelCased. */
export type GpcBandValue = 'at_or_below_mpt' | 'over_mpt_to_25k';

/**
 * Result of the Stage-3 auto open-market price search
 * (`POST /api/gpc/prices/search`). The ≥3 price-history is satisfied by the
 * search RESULTS (`fromSearch=true`); `unitPrice` is the derived (median)
 * representative basis for the later total.
 */
export interface GpcPriceSearchResult {
  priceQuotes: GpcPriceQuote[];
  unitPrice: number | null;
  /** Durable server generation used to fence threshold writes after reruns. */
  searchGeneration?: string;
  /**
   * The AGENT-determined buy type (Phase 8 [NOTES-3]) — determined during the
   * price search (the requirement text is available there) and surfaced as the
   * editable Stage-3 default. Defaults `supply` when the agent gracefully fails.
   */
  buyType: GpcBuyParams['buy_type'];
  /**
   * `true` when `buyType` was produced by the agent; `false` on the graceful
   * supply fallback (UI flags it as needs-confirmation) or once the user edits.
   */
  buyTypeAgentSource: boolean;
  /**
   * Number of returned quotes carrying a non-null price (M2, 2026-06-22).
   */
  pricedCount: number;
  /**
   * `true` when fewer than 3 priced sources were found (M2, 2026-06-22). GPC
   * documents price reasonableness with ≥3 sources; this is a NON-BLOCKING flag
   * advising the user to add/verify additional sources manually — NOT a gate.
   */
  insufficientPriceHistory: boolean;
}

/**
 * Result of the Stage-3 threshold determination
 * (`POST /api/gpc/threshold/determine`). Computes `total = unit_price × quantity`,
 * derives the band/path, and carries the user-facing next-steps message. This
 * completes the GPC flow this iteration (no Path-B decide / eCARS — a >$15K
 * result is surfaced ONLY as `nextStepsMessage`).
 */
export interface GpcThresholdResult {
  buyParams: GpcBuyParams;
  /** The derived total (`unit_price × quantity`), or null when inputs missing. */
  total: number | null;
  band: GpcBandValue | null;
  sourcingPath: GpcSourcingPath | null;
  /**
   * The buy-type-specific micro-purchase threshold (supply $15,000 /
   * service $2,500 / construction $2,000), echoed from the backend so the
   * band label can show the correct pivot figure. `null`/absent on older
   * persisted flows or need-more-info results that predate this field.
   */
  microPurchaseThreshold?: number | null;
  nextStepsMessage: string;
  /**
   * The determined band's always-required per-transaction obligations (H1,
   * 2026-06-22): the 889 representation, GPC Abstract / AO control number, price
   * reasonableness, etc. Surfaced so the ≤$15K path is no longer silent on them.
   * Empty when no band is determined (need-more-info / out-of-scope).
   */
  obligations: string[];
  needMoreInfo: boolean;
  outOfScope: boolean;
}

/** eCARS approval sign-off state — mirrors the backend `GpcApproval` model. */
export interface GpcApproval {
  required: boolean;
  state: 'not_required' | 'pending' | 'approved' | 'rejected';
  approving_official: string | null;
  gpc_pm_notified: boolean;
  gpc_pm_name: string;
  routed_at: string | null;
  decided_at: string | null;
  notes: string;
}

/**
 * Result of `requirements/questions`: questions + count.
 *
 * Phase 8 ([NOTES-3]): Stage 1 is item-only — there is no buy-param echo
 * (dollar/quantity/buy-type are no longer Stage-1 inputs; buy type is
 * agent-determined in Stage 3, quantity is a Stage-3 input).
 */
export interface GpcQuestionsResult {
  questions: Question[];
  num_items_detected: number;
}

/**
 * Build an `Error` carrying the error-contract fields. Callers surface
 * `err.message` through `toUserFacingError()`; the two extra fields are for
 * analytics only and must never be rendered.
 */
function buildContractError(resp: {
  error?: string;
  error_code?: string;
  technical_detail?: string;
}): Error & { error_code?: string; technical_detail?: string } {
  const err = new Error(
    resp.error ?? USER_FACING_ERROR_MESSAGES.maintainers_alerted,
  ) as Error & { error_code?: string; technical_detail?: string };
  err.error_code = resp.error_code;
  err.technical_detail = resp.technical_detail;
  return err;
}

// ---------------------------------------------------------------------------
// Stage 800 — Clarify Requirements
// ---------------------------------------------------------------------------

/**
 * Generate GPC clarifying questions for a requirement (Stage 1, item-only).
 *
 * Phase 8 ([NOTES-3]): this is ordinary item/requirement elicitation — no
 * buy-param plumbing. Dollar amount / quantity / buy type are NOT captured here
 * (quantity is a Stage-3 input; buy type is agent-determined in Stage 3; the
 * dollar total is derived in Stage 3 from the searched price × quantity).
 */
export async function getGpcQuestions(
  requirementText: string,
  flowId?: string | null,
): Promise<GpcQuestionsResult> {
  const response = await apiCall<Question[]>(
    '/api/gpc/requirements/questions',
    'POST',
    {
      requirement_text: requirementText,
      flow_id: flowId ?? null,
    },
  );

  if (!response.success) {
    throw buildContractError(response);
  }

  return {
    questions: (response.data as Question[]) ?? [],
    num_items_detected: (response as any).num_items_detected ?? 1,
  };
}

/** Persist GPC clarifying-question answers. */
export async function saveGpcAnswers(
  flowId: string,
  questions: Question[],
  answers: Record<string, string>,
  requirementText?: string,
): Promise<{ answers_saved: number }> {
  const response = await apiCall<{ answers_saved: number }>(
    '/api/gpc/requirements/save-answers',
    'POST',
    {
      flow_id: flowId,
      questions,
      answers,
      ...(requirementText !== undefined && { requirement_text: requirementText }),
    },
  );

  if (!response.success) {
    throw buildContractError(response);
  }
  return (response.data as { answers_saved: number }) ?? { answers_saved: 0 };
}

// ---------------------------------------------------------------------------
// Stage 801 — Salient Characteristics (MFR)
// ---------------------------------------------------------------------------

/**
 * Map the backend's MR-parity snake_case MFR dict into the camelCase `MFR`
 * object the forked `GPCSalientCharacteristics` component reads.
 *
 * The backend (both the fresh `generate-mfr` response AND the persisted stage-801
 * output) carries `requirements_text` / `requirements_list` / `removed_specs` /
 * `cost_savings_estimate`. The component reads `rawText` / `removedSpecs` /
 * `costSavingsEstimate`; without this mapping those fields are `undefined` and
 * the "Removed Overspecifications" / "Estimated Cost Savings" alerts never
 * render. This is the SINGLE source of truth for the mapping so the generate
 * path and the flow-reopen restore path (App.tsx) can't drift apart.
 *
 * `opts.id` lets the restore path supply a deterministic, flow-stable id.
 */
export function mapMfrResponse(
  data: any,
  requirementText: string,
  questionsAndAnswers: { question: Question; answer: string }[] = [],
  opts?: { id?: string },
): MFR {
  const safe = data ?? {};
  // Accept `requirements_text` (fresh + persisted), `minimum_requirements`
  // (MR-shaped persistence), or `rawText` (already-mapped) for resilience.
  const requirementsText: string =
    safe.requirements_text ?? safe.minimum_requirements ?? safe.rawText ?? '';
  const requirementsList: string[] = Array.isArray(safe.requirements_list)
    ? safe.requirements_list
    : [];
  const removedSpecs = Array.isArray(safe.removed_specs)
    ? safe.removed_specs.filter((item: unknown): item is string => typeof item === 'string')
    : Array.isArray(safe.removedSpecs)
      ? safe.removedSpecs.filter((item: unknown): item is string => typeof item === 'string')
      : [];
  const overspecDecisions = mapOverspecDecisions(
    safe.overspec_decisions ?? safe.overspecDecisions,
    removedSpecs,
  );

  return {
    id: opts?.id ?? `gpc-mfr-${Date.now()}`,
    commodityDescription: requirementText,
    generatedAt: new Date().toISOString(),
    requirements: {
      functional: requirementsList,
      performance: [],
    },
    questionsAnswered: questionsAndAnswers.map((qa) => ({
      questionId: qa.question.id,
      question: qa.question.question,
      answer: qa.answer,
      category: qa.question.category,
    })),
    rawText: requirementsText,
    originalCommodityDescription: requirementText,
    removedSpecs,
    costSavingsEstimate: safe.cost_savings_estimate ?? safe.costSavingsEstimate ?? '',
    ...(overspecDecisions !== undefined ? { overspecDecisions } : {}),
    ...(typeof (safe.salient_revision ?? safe.salientRevision) === 'number'
      ? { salientRevision: safe.salient_revision ?? safe.salientRevision }
      : {}),
  };
}

/**
 * Synthesize the GPC salient characteristics from the Q&A. The `questionsAndAnswers`
 * entries mirror the MR shape: `{ question: Question, answer: string }`.
 *
 * Delegates the snake_case→`MFR` transform to `mapMfrResponse` (shared with the
 * flow-reopen restore path in App.tsx).
 */
export async function generateGpcSalientCharacteristics(
  requirementText: string,
  questionsAndAnswers: { question: Question; answer: string }[],
  flowId?: string | null,
): Promise<MFR> {
  const response = await apiCall<any>(
    '/api/gpc/requirements/generate-mfr',
    'POST',
    {
      requirement_text: requirementText,
      questions_and_answers: questionsAndAnswers,
      flow_id: flowId ?? null,
    },
  );

  if (!response.success || !response.data) {
    throw buildContractError(response);
  }

  return mapMfrResponse(response.data, requirementText, questionsAndAnswers);
}

/** Persist edited GPC salient-characteristics text without recomputing. */
export async function saveGpcSalientCharacteristics(
  flowId: string,
  requirementsText: string,
  options?: {
    expectedSalientRevision?: number;
    overspecAction?: {
      action: 'restore';
      overspecId: string;
      restoredRequirement: string;
      restorationReason: string;
    };
  },
): Promise<Record<string, any>> {
  const response = await apiCall<Record<string, any>>(
    '/api/gpc/requirements/save-mfr',
    'POST',
    {
      flow_id: flowId,
      requirements_text: requirementsText,
      ...(typeof options?.expectedSalientRevision === 'number'
        ? { expected_salient_revision: options.expectedSalientRevision }
        : {}),
      ...(options?.overspecAction ? {
        overspec_action: {
          action: options.overspecAction.action,
          overspec_id: options.overspecAction.overspecId,
          restored_requirement: options.overspecAction.restoredRequirement,
          restoration_reason: options.overspecAction.restorationReason,
        },
      } : {}),
    },
  );

  if (!response.success) {
    const error = buildContractError(response) as Error & { data?: Record<string, any> };
    if (response.data !== undefined) error.data = response.data;
    throw error;
  }
  return response.data ?? { requirements_text: requirementsText };
}

// ---------------------------------------------------------------------------
// Stage 802 — Product Discovery
// ---------------------------------------------------------------------------

/**
 * Per-source run outcome for the (optionally scoped) GPC product search. Mirrors
 * the backend `source_status` map values:
 *   - `'blocked'` — the source site blocked the scrape (WAF/Cloudflare 403 /
 *     challenge / scrape exception); it could NOT be searched automatically.
 *   - `'empty'`   — the source ran cleanly but returned 0 matching products.
 *   - `'ok'`      — the source returned products.
 */
export type GpcSourceStatus = 'blocked' | 'unavailable' | 'empty' | 'ok';

/**
 * Result of the (optionally source-scoped) GPC product search. `products` is the
 * bare merged product list (unchanged behavior). The two additive fields surface
 * the backend's per-source run status so the Mandatory Source stage can tell
 * "the source was BLOCKED by its site" apart from "ran cleanly and found 0":
 *   - `sourceStatus` maps canonical lowercase source key → `GpcSourceStatus`,
 *     e.g. `{ unicor: 'blocked', gsa_advantage: 'ok' }`.
 *   - `blockedSources` is the list of source keys that were blocked,
 *     e.g. `['unicor']`.
 */
export type GpcCarpetIiCheck = import('./stageFunctions').ProductSourceResults['items'][number]['checks'][string];

export function restoreGpcCarpetCheck(output: unknown): GpcCarpetIiCheck | null {
  const check = output && typeof output === 'object' ? (output as { carpet_ii_check?: unknown }).carpet_ii_check : null;
  return check && typeof check === 'object' && !Array.isArray(check)
    && typeof (check as { status?: unknown }).status === 'string' ? check as GpcCarpetIiCheck : null;
}

export interface GpcProductSearchResult {
  products: Product[];
  sourceStatus: Record<string, GpcSourceStatus>;
  blockedSources: string[];
  carpetIiCheck?: GpcCarpetIiCheck | null;
}

/**
 * Discover products for a GPC requirement.
 *
 * When a confirmed `GpcSourceDecision` is supplied (decide-first flow), its
 * source fields are threaded to the backend so the search is SCOPED to the
 * mandated channel (the backend derives the skip-flags via
 * `search_scope_for_source`). When omitted, the backend runs the full
 * (unscoped) search for back-compat.
 *
 * Returns the bare product list plus the backend's additive top-level
 * `source_status` / `blocked_sources` fields (snake→camel-mapped) so the caller
 * can distinguish a blocked source from an empty one.
 */
export async function searchGpcProducts(
  requirements: string,
  flowId?: string | null,
  numItemsDetected = 1,
  decision?: GpcSourceDecision | null,
): Promise<GpcProductSearchResult> {
  const response = await apiCall<Product[]>(
    '/api/gpc/products/search',
    'POST',
    {
      requirements,
      flow_id: flowId ?? null,
      num_items_detected: numItemsDetected,
      ...(decision
        ? {
            recommended_source: decision.recommended_source ?? null,
            user_override_source: decision.user_override_source ?? null,
            verify_flags: decision.verify_flags ?? [],
            selected_sources: decision.selected_sources ?? [],
          }
        : {}),
    },
  );

  if (!response.success) {
    throw buildContractError(response);
  }

  // `source_status` / `blocked_sources` are ADDITIVE top-level fields on the
  // ApiResponse (like `num_items_detected`), NOT inside `data`. Read them
  // defensively so an older payload (without them) yields safe empties.
  const rawStatus = (response as any).source_status;
  const sourceStatus: Record<string, GpcSourceStatus> =
    rawStatus && typeof rawStatus === 'object' && !Array.isArray(rawStatus)
      ? (rawStatus as Record<string, GpcSourceStatus>)
      : {};
  const rawBlocked = (response as any).blocked_sources;
  const blockedSources: string[] = Array.isArray(rawBlocked)
    ? (rawBlocked as string[])
    : [];

  return {
    products: (response.data as Product[]) ?? [],
    sourceStatus,
    blockedSources,
    carpetIiCheck: restoreGpcCarpetCheck(response),
  };
}

/**
 * Persist the cardholder's GPC product selection.
 *
 * `selectedIds` is the competition set (defaults to ALL discovered product ids
 * upstream); `awardProductId` is the explicit single award (the radio pick); and
 * `awardOverrideReason` is the required free-text justification when the award is
 * NOT the lowest-priced checked product (else ""). The last arg is optional so
 * existing callers stay source-compatible.
 */
export async function saveGpcProductSelection(
  flowId: string,
  selectedIds: string[],
  selectedKeys: string[] = [],
  awardProductId: string = '',
  awardOverrideReason: string = '',
): Promise<{
  selected_ids: string[];
  selected_keys: string[];
  award_product_id: string;
  award_override_reason: string;
}> {
  const response = await apiCall<{
    selected_ids: string[];
    selected_keys: string[];
    award_product_id: string;
    award_override_reason: string;
  }>('/api/gpc/products/selection', 'POST', {
    flow_id: flowId,
    selected_ids: selectedIds,
    selected_keys: selectedKeys,
    award_product_id: awardProductId,
    award_override_reason: awardOverrideReason,
  });

  if (!response.success) {
    throw buildContractError(response);
  }
  return (
    (response.data as {
      selected_ids: string[];
      selected_keys: string[];
      award_product_id: string;
      award_override_reason: string;
    }) ?? {
      selected_ids: selectedIds,
      selected_keys: selectedKeys,
      award_product_id: awardProductId,
      award_override_reason: awardOverrideReason,
    }
  );
}

/**
 * Designate ONE selected listing as the award and enrich it via SAM.gov.
 *
 * Resolves the award listing from the persisted Mandatory Source stage (804),
 * enriches it (SAM small-business + business types), persists the award vendor
 * and `award_product_id` back onto stage 804, and returns the resolved
 * `GpcVendor`. `is_small_business` may be `null` when SAM did not return a
 * status for the resolved NAICS — the caller must then confirm it manually.
 */
export async function enrichGpcAwardVendor(
  flowId: string,
  opts?: { awardProductId?: string; naics?: string; psc?: string; sizeStandard?: string },
): Promise<GpcVendor> {
  const response = await apiCall<any>('/api/gpc/mandatory-source/enrich-award', 'POST', {
    flow_id: flowId,
    award_product_id: opts?.awardProductId ?? '',
    naics: opts?.naics ?? '',
    psc: opts?.psc ?? '',
    size_standard: opts?.sizeStandard ?? '',
  });
  if (!response.success) {
    throw buildContractError(response);
  }
  return mapGpcVendor(response.data);
}

/**
 * Optional helper: surface additional candidate sources/vendors for the buy's
 * NAICS/PSC so the cardholder can consider more priced listings. Returns the
 * raw candidate vendors (mapped through `mapGpcVendor`).
 */
export async function findMoreGpcSources(
  flowId: string,
  opts?: { naics?: string; psc?: string },
): Promise<{ candidates: GpcVendor[] }> {
  const response = await apiCall<any>('/api/gpc/sources/find-more', 'POST', {
    flow_id: flowId,
    naics: opts?.naics ?? '',
    psc: opts?.psc ?? '',
  });
  if (!response.success) {
    throw buildContractError(response);
  }
  const data = response.data ?? {};
  const candidates: GpcVendor[] = Array.isArray(data.candidates)
    ? data.candidates.map(mapGpcVendor)
    : [];
  return { candidates };
}

/** Display-only business type for a single product row in the mandatory-source
 *  table. `is_small_business` is `null` when the product's seller could not be
 *  matched to a discovered vendor (NEVER silently `true`). */
export interface GpcProductBusinessType {
  business_types: string[];
  is_small_business: boolean | null;
}

/**
 * Per-product Section 889 covered-telecommunications eligibility status (FAR
 * 52.204-26). This is an independent VENDOR-ELIGIBILITY check — whether the
 * vendor represents it does NOT provide/use covered telecom equipment/services
 * (Huawei / ZTE / Kaspersky-type) — and has nothing to do with price. Mirrors
 * the backend `section_889` block attached to each product:
 *   - `status` — `'compliant'` (vendor represents NO covered telecom),
 *     `'non_compliant'` (vendor provides/uses covered telecom), `'no_representation'`
 *     (no 889 rep on file), or `'unknown'` (could not determine — verify manually).
 *   - `represents_no_covered_telecom` — the raw 52.204-26 representation, `null`
 *     when unknown.
 *   - `source` — where the status was looked up (`'sam'` / `'gsa_easy_search'`).
 *   - `uei` / `checked_at` — provenance metadata.
 */
export interface GpcProductSection889 {
  status: 'compliant' | 'non_compliant' | 'no_representation' | 'unknown';
  represents_no_covered_telecom: boolean | null;
  source: 'sam' | 'gsa_easy_search' | null;
  uei: string | null;
  checked_at: string | null;
}

/**
 * Enrich the mandatory-source product table with each product's Section 889
 * covered-telecommunications eligibility status. POSTs to
 * `/api/gpc/mandatory-source/enrich-889`; the backend looks up the vendor's
 * 52.204-26 representation (SAM / GSA 889 Easy Search) and returns the products
 * with a `section_889` block stamped onto each. Best-effort / non-blocking: the
 * caller merges the returned `section_889` onto its displayed rows; on failure
 * the rows simply show "Verify manually".
 *
 * Returns the bare product list (each carrying an optional `section_889`).
 */
export async function enrichGpc889(flowId: string): Promise<Product[]> {
  const response = await apiCall<Product[]>('/api/gpc/mandatory-source/enrich-889', 'POST', {
    flow_id: flowId,
  });
  if (!response.success) {
    throw buildContractError(response);
  }
  return (response.data as Product[]) ?? [];
}

/**
 * BATCH business-type enrichment for the mandatory-source product table. Runs
 * `discover_vendors` ONCE for the flow's NAICS, then matches each stage-804
 * product's seller name against the discovered vendors. Returns a display-only
 * map keyed by product id; unmatched products map to `is_small_business: null`
 * + empty `business_types`. Persists nothing.
 */
export async function enrichGpcMandatoryVendors(
  flowId: string,
  opts?: { naics?: string; psc?: string },
): Promise<Record<string, GpcProductBusinessType>> {
  const response = await apiCall<any>('/api/gpc/mandatory-source/enrich-vendors', 'POST', {
    flow_id: flowId,
    naics: opts?.naics ?? '',
    psc: opts?.psc ?? '',
  });
  if (!response.success) {
    throw buildContractError(response);
  }
  const data = response.data ?? {};
  const out: Record<string, GpcProductBusinessType> = {};
  Object.keys(data).forEach((pid) => {
    const v = data[pid] ?? {};
    out[String(pid)] = {
      business_types: Array.isArray(v.business_types)
        ? v.business_types.filter((bt: any): bt is string => typeof bt === 'string')
        : [],
      is_small_business:
        v.is_small_business === true || v.is_small_business === false
          ? v.is_small_business
          : null,
    };
  });
  return out;
}

// ---------------------------------------------------------------------------
// Stage 802 (Phase 8, [NOTES-3]) — auto price search → threshold determination
// ---------------------------------------------------------------------------

/**
 * Auto-search open-market sources (Home Depot / Google / local vendors) for
 * PRICES (`POST /api/gpc/prices/search`). The ≥3 price-history is satisfied by
 * the search RESULTS (`from_search=true`), not user entry; a representative
 * `unitPrice` (median) basis is derived for the later total.
 *
 * Mirrors the snake→camel mapping convention; the price quotes keep their own
 * snake_case shape (consumed by the read-only list and `mapPriceQuote`).
 */
export async function searchGpcPrices(
  flowId: string,
  requirementText: string,
  numItemsDetected = 1,
  options: { forceRefresh?: boolean; manualBuyType?: GpcBuyParams['buy_type'] } = {},
): Promise<GpcPriceSearchResult> {
  const response = await apiCall<{
    price_quotes?: any[];
    unit_price?: number | null;
    buy_type?: GpcBuyParams['buy_type'];
    buy_type_agent_source?: boolean;
    priced_count?: number;
    insufficient_price_history?: boolean;
    price_search?: { generation?: string };
  }>(
    '/api/gpc/prices/search',
    'POST',
    {
      flow_id: flowId,
      requirement_text: requirementText,
      num_items_detected: numItemsDetected,
      // A user-requested re-run must bypass the stage-802 cache. Initial and
      // restored searches intentionally keep the normal cache behavior.
      force_refresh: options.forceRefresh === true,
      ...(options.manualBuyType ? { manual_buy_type: options.manualBuyType } : {}),
    },
  );

  if (!response.success) {
    throw buildContractError(response);
  }
  const data = (response.data as {
    price_quotes?: any[];
    unit_price?: number | null;
    buy_type?: GpcBuyParams['buy_type'];
    buy_type_agent_source?: boolean;
    priced_count?: number;
    insufficient_price_history?: boolean;
    price_search?: { generation?: string };
  }) ?? {};
  const priceQuotes = Array.isArray(data.price_quotes) ? data.price_quotes.map(mapPriceQuote) : [];
  // Prefer the backend-computed count; fall back to counting priced quotes so an
  // older payload (no flag) still yields a sensible value.
  const pricedCount =
    typeof data.priced_count === 'number'
      ? data.priced_count
      : priceQuotes.filter((q) => q.price !== null && q.price !== undefined).length;
  return {
    priceQuotes,
    unitPrice: data.unit_price ?? null,
    searchGeneration: data.price_search?.generation,
    buyType: data.buy_type ?? null,
    buyTypeAgentSource: !!data.buy_type_agent_source,
    pricedCount,
    insufficientPriceHistory:
      typeof data.insufficient_price_history === 'boolean'
        ? data.insufficient_price_history
        : pricedCount < 3,
  };
}

/**
 * Map the backend threshold-determine dict (snake_case) into the camelCase
 * `GpcThresholdResult` the Stage-3 result panel reads.
 */
export function mapThresholdResult(data: any): GpcThresholdResult {
  const safe = data ?? {};
  const buyParams: GpcBuyParams = safe.buy_params
    ? {
        requirement_title: safe.buy_params.requirement_title ?? '',
        unit_price: safe.buy_params.unit_price ?? null,
        quantity: safe.buy_params.quantity ?? null,
        dollar_amount: safe.buy_params.dollar_amount ?? null,
        buy_type: safe.buy_params.buy_type ?? null,
        buy_type_agent_source: !!safe.buy_params.buy_type_agent_source,
      }
    : {};
  return {
    buyParams,
    total: safe.dollar_amount ?? null,
    band: safe.threshold_band ?? null,
    sourcingPath: safe.sourcing_path ?? null,
    microPurchaseThreshold: safe.micro_purchase_threshold ?? null,
    nextStepsMessage: composeGpcNextSteps({
      needMoreInfo: !!safe.need_more_info,
      outOfScope: !!safe.out_of_scope,
      sourcingPath: safe.sourcing_path ?? null,
      buyType: safe.buy_params?.buy_type ?? null,
      microPurchaseThreshold: safe.micro_purchase_threshold ?? null,
    }),
    obligations: Array.isArray(safe.obligations) ? safe.obligations : [],
    needMoreInfo: !!safe.need_more_info,
    outOfScope: !!safe.out_of_scope,
  };
}

/**
 * Determine the threshold (`POST /api/gpc/threshold/determine`): compute
 * `total = unit_price × quantity`, derive the band + sourcing path, and return
 * the next-steps message. This is the canonical GPC completion (stage 802;
 * `GPC_MAX_STAGES = 3`). A >$15K (Path B) result is surfaced ONLY as the
 * next-steps message this iteration (no decide hierarchy / eCARS).
 */
export async function determineGpcThreshold(
  flowId: string,
  params: {
    unitPrice: number | null;
    quantity: number | null;
    buyType: GpcBuyParams['buy_type'];
    requirementTitle?: string;
    searchGeneration?: string;
  },
): Promise<GpcThresholdResult> {
  const response = await apiCall<any>(
    '/api/gpc/threshold/determine',
    'POST',
    {
      flow_id: flowId,
      unit_price: params.unitPrice ?? null,
      quantity: params.quantity ?? null,
      buy_type: params.buyType ?? null,
      requirement_title: params.requirementTitle ?? '',
      search_generation: params.searchGeneration ?? null,
    },
  );

  if (!response.success) {
    throw buildContractError(response);
  }
  return mapThresholdResult(response.data);
}

// ---------------------------------------------------------------------------
// DEFERRED (Phase 7 design, NOT in the active flow) — decide engine, manual
// ≥3-quote capture, and eCARS approval. These client functions are retained
// for the eventual >$15K build but are NOT called by the active 3-stage flow.
// ---------------------------------------------------------------------------

/**
 * Run the deterministic GPC decide engine. The cardholder's confirm/override
 * (`userConfirmed` / `userOverrideSource`) is threaded onto the returned
 * decision and persisted. A confirmed call is the canonical GPC completion
 * (backend stage 803).
 */
export async function decideGpcSource(
  requirementText: string,
  buyParams: GpcBuyParams,
  opts?: {
    flowId?: string | null;
    vendorIsSmall?: boolean;
    userConfirmed?: boolean;
    userOverrideSource?: string | null;
    /** Multi-select mandatory-source picks (checkboxes) → `selected_sources`. */
    selectedSources?: string[];
  },
): Promise<GpcSourceDecision> {
  const response = await apiCall<GpcSourceDecision>(
    '/api/gpc/decide/source',
    'POST',
    {
      requirement_text: requirementText,
      flow_id: opts?.flowId ?? null,
      dollar_amount: buyParams.dollar_amount ?? null,
      quantity: buyParams.quantity ?? null,
      buy_type: buyParams.buy_type ?? null,
      requirement_title: buyParams.requirement_title ?? null,
      vendor_is_small: opts?.vendorIsSmall ?? true,
      user_confirmed: opts?.userConfirmed ?? false,
      user_override_source: opts?.userOverrideSource ?? null,
      ...(opts?.selectedSources !== undefined
        ? { selected_sources: opts.selectedSources }
        : {}),
    },
  );

  if (!response.success) {
    throw buildContractError(response);
  }
  return normalizeDecision(response.data);
}

/**
 * Canonical Path-B mandatory-source hierarchy — TS mirror of the backend
 * `PATH_B_SOURCE_HIERARCHY` (`farchat/common/gpc_decision_rules.py`). ORDER is
 * load-bearing (FAR-8 order of precedence). UNICOR + AbilityOne are WIRED
 * (searchable in Waypoint) and sit in their FAR-8 positions above GSA. Keep in
 * lockstep with the backend constant.
 */
export const PATH_B_SOURCE_HIERARCHY: string[] = [
  'supply_system',
  'unicor',
  'abilityone',
  'af_advantage',
  'gsa_advantage',
];

/**
 * Refresh a Path-B decision's `source_hierarchy` against the CURRENT canonical
 * hierarchy so newly-wired available sources (e.g. UNICOR / AbilityOne) always
 * surface — even on an older flow whose persisted decision predates the wiring
 * (a stale 3-entry snapshot). We return the canonical order, but ONLY keep the
 * keys that are present in EITHER the decision's own hierarchy OR the canonical
 * list, then append any decision-only keys the canonical list doesn't know about
 * (forward-compat). On a non-Path-B (or hierarchy-less) decision this is a no-op,
 * so Path-A / need-more-info decisions are untouched.
 */
function refreshSourceHierarchy(
  sourcingPath: GpcSourcingPath | null,
  hierarchy: string[],
): string[] {
  // Only Path B carries a mandatory-source hierarchy; never synthesize one for
  // Path A / undecided (an empty hierarchy must stay empty so the component's
  // on-mount re-decide effect still fires when nothing was supplied).
  if (sourcingPath !== 'mandatory_source') return hierarchy;
  if (!Array.isArray(hierarchy) || hierarchy.length === 0) return hierarchy;
  // Union in canonical order: the full canonical list (so unicor/abilityone appear
  // on stale flows), followed by any decision-only keys not in the canonical list
  // (forward-compat — never drop a source the backend emitted).
  const extras = hierarchy.filter((k) => !PATH_B_SOURCE_HIERARCHY.includes(k));
  return [...PATH_B_SOURCE_HIERARCHY, ...extras];
}

/**
 * Coerce the backend decision JSON into a fully-populated `GpcSourceDecision`,
 * defaulting the Phase-7 fields so older/partial payloads don't yield `undefined`
 * (the component branches on `sourcing_path` and reads `source_hierarchy`).
 *
 * On a Path-B decision the `source_hierarchy` is refreshed against the canonical
 * `PATH_B_SOURCE_HIERARCHY` (via `refreshSourceHierarchy`) so newly-wired
 * available sources (UNICOR / AbilityOne) appear even when a persisted/restored
 * decision predates their wiring (a stale 3-entry snapshot).
 */
export function normalizeDecision(data: any): GpcSourceDecision {
  const safe = data ?? {};
  const sourcingPath: GpcSourcingPath | null = safe.sourcing_path ?? null;
  const rawHierarchy: string[] = Array.isArray(safe.source_hierarchy)
    ? safe.source_hierarchy
    : [];
  return {
    sourcing_path: sourcingPath,
    category: safe.category ?? '',
    recommended_source: safe.recommended_source ?? '',
    source_hierarchy: refreshSourceHierarchy(sourcingPath, rawHierarchy),
    order_of_precedence: Array.isArray(safe.order_of_precedence) ? safe.order_of_precedence : [],
    threshold_band: safe.threshold_band ?? null,
    obligations: Array.isArray(safe.obligations) ? safe.obligations : [],
    requires_dd2579: !!safe.requires_dd2579,
    requires_889_verification: !!safe.requires_889_verification,
    requires_ecars: !!safe.requires_ecars,
    verify_flags: Array.isArray(safe.verify_flags) ? safe.verify_flags : [],
    user_confirmed: !!safe.user_confirmed,
    user_override_source: safe.user_override_source ?? null,
    selected_sources: Array.isArray(safe.selected_sources) ? safe.selected_sources : [],
  };
}

// ---------------------------------------------------------------------------
// Stage 802 (Path A) — ≥3 price-quote capture
// ---------------------------------------------------------------------------

/**
 * Map the backend's snake_case price-quote dict into the `GpcPriceQuote` shape
 * the grid reads (the backend JSON is snake_case; mirror MR's snake→camel
 * convention). Defaults keep partial rows valid.
 */
export function mapPriceQuote(data: any): GpcPriceQuote {
  const safe = data ?? {};
  // Contact-for-pricing (2026-06-26): default `price_status` to `'found'` so a
  // legacy payload (no field) keeps the verified-price rendering; map the nested
  // `vendor_contact` defensively (null when absent / not an object).
  const status: GpcPriceStatus =
    safe.price_status === 'on_request' || safe.price_status === 'not_found'
      ? safe.price_status
      : 'found';
  const rawContact = safe.vendor_contact;
  const vendorContact: GpcVendorContact | null =
    rawContact && typeof rawContact === 'object' && !Array.isArray(rawContact)
      ? {
          phone: rawContact.phone ?? null,
          email: rawContact.email ?? null,
          quote_url: rawContact.quote_url ?? null,
          contact_url: rawContact.contact_url ?? null,
        }
      : null;
  return {
    source: safe.source ?? '',
    vendor_name: safe.vendor_name ?? '',
    price: safe.price ?? null,
    url: safe.url ?? null,
    is_small_disadvantaged_business: !!safe.is_small_disadvantaged_business,
    on_sam_gov: safe.on_sam_gov ?? null,
    from_search: safe.from_search ?? true,
    captured_at: safe.captured_at ?? null,
    acceptable: safe.acceptable ?? null,
    rationale: safe.rationale ?? null,
    price_status: status,
    vendor_contact: vendorContact,
    // Derive a sensible default when the backend omits the flag: any quote with
    // no verified price requires the user to enter one manually.
    requires_manual_price:
      typeof safe.requires_manual_price === 'boolean'
        ? safe.requires_manual_price
        : safe.price === null || safe.price === undefined,
  };
}

/**
 * Persist Path-A (open-market) price quotes. Stored INLINE in the 802
 * product-discovery record (`POST /api/gpc/quotes/save`). The ≥3-quote
 * completion gate is enforced both client-side (before Continue) and at the
 * data-model layer.
 */
export async function saveGpcPriceQuotes(
  flowId: string,
  quotes: GpcPriceQuote[],
  competition = false,
): Promise<{ price_quotes: GpcPriceQuote[]; count: number; source_count?: number }> {
  const response = await apiCall<{
    price_quotes?: any[];
    competition_quotes?: any[];
    count: number;
    source_count?: number;
  }>('/api/gpc/quotes/save', 'POST', {
    flow_id: flowId,
    quotes,
    ...(competition ? { competition: true } : {}),
  });

  if (!response.success) {
    throw buildContractError(response);
  }
  const data =
    (response.data as {
      price_quotes?: any[];
      competition_quotes?: any[];
      count?: number;
      source_count?: number;
    }) ?? {};
  // Path B (competition=true) returns `competition_quotes` + a derived
  // `source_count`; Path A returns `price_quotes`. Normalize to `price_quotes`.
  const raw = Array.isArray(data.competition_quotes)
    ? data.competition_quotes
    : Array.isArray(data.price_quotes)
      ? data.price_quotes
      : [];
  const mapped = raw.map(mapPriceQuote);
  return {
    price_quotes: mapped,
    count: data.count ?? mapped.length,
    ...(data.source_count !== undefined ? { source_count: data.source_count } : {}),
  };
}

// ---------------------------------------------------------------------------
// Stage 803 (Path B only) — eCARS approval routing
// ---------------------------------------------------------------------------

/** Map the backend's snake_case approval dict into the `GpcApproval` shape. */
export function mapApproval(data: any): GpcApproval {
  const safe = data ?? {};
  return {
    required: !!safe.required,
    state: safe.state ?? 'not_required',
    approving_official: safe.approving_official ?? null,
    gpc_pm_notified: !!safe.gpc_pm_notified,
    gpc_pm_name: safe.gpc_pm_name ?? 'Valerie Clark',
    routed_at: safe.routed_at ?? null,
    decided_at: safe.decided_at ?? null,
    notes: safe.notes ?? '',
  };
}

/**
 * Route the prepared Path-B package to eCARS, creating the PENDING approval at
 * stage 803 (`POST /api/gpc/approval/route`). Notifies Ms. Clark (the GPC PM).
 */
export async function routeGpcApproval(
  flowId: string,
  opts?: {
    approvingOfficial?: string | null;
    installationProfileId?: string;
    notes?: string;
    sourcingPath?: GpcSourcingPath | null;
  },
): Promise<GpcApproval> {
  const response = await apiCall<any>(
    '/api/gpc/approval/route',
    'POST',
    {
      flow_id: flowId,
      approving_official: opts?.approvingOfficial ?? null,
      installation_profile_id: opts?.installationProfileId ?? 'moody_23cons',
      notes: opts?.notes ?? '',
      ...(opts?.sourcingPath ? { sourcing_path: opts.sourcingPath } : {}),
    },
  );

  if (!response.success) {
    throw buildContractError(response);
  }
  return mapApproval(response.data);
}

/**
 * Read the current eCARS approval state (stage 803) via
 * `GET /api/gpc/approval/state`. Returns an empty/`not_required` approval when
 * nothing has been routed yet.
 */
export async function getGpcApprovalState(flowId: string): Promise<GpcApproval> {
  const response = await apiCall<any>(
    `/api/gpc/approval/state?flow_id=${encodeURIComponent(flowId)}`,
    'GET',
  );

  if (!response.success) {
    throw buildContractError(response);
  }
  return mapApproval(response.data);
}

/**
 * Transition the eCARS approval state (`POST /api/gpc/approval/state`). Only
 * `pending → approved` / `pending → rejected` are legal; terminal states are
 * immutable (the backend surfaces a 400 on an illegal transition).
 */
export async function setGpcApprovalState(
  flowId: string,
  state: 'approved' | 'rejected',
  opts?: { approvingOfficial?: string | null; notes?: string | null },
): Promise<GpcApproval> {
  const response = await apiCall<any>(
    '/api/gpc/approval/state',
    'POST',
    {
      flow_id: flowId,
      state,
      approving_official: opts?.approvingOfficial ?? null,
      notes: opts?.notes ?? null,
    },
  );

  if (!response.success) {
    throw buildContractError(response);
  }
  return mapApproval(response.data);
}

// ===========================================================================
// GPC PER-FORM STAGES (S4–S11) — dynamic stepper + per-form persistence.
//
// Contract: `issues/20260622-gpc-per-form-stages_contract.md`. Backend routes
// live in `backend/gpc_routes.py`. Per the contract §4, the form TS interfaces
// keep SNAKE_CASE keys 1:1 with the Pydantic models (mirroring the shipped
// `GpcPriceQuote` / `GpcApproval` convention); each `map<Model>(data)` helper
// defaults every field exactly like `mapPriceQuote` / `mapApproval`.
//
// Stage code map (backend `flow_storage.py` / `gpc_decision_rules.py`) — CLEAN
// CONTIGUOUS scheme (2026-06-24 refactor — NAICS/PSC + Quotes; no vendor stage):
//   803 = naics_psc (NEW, both paths) · 804 = mandatory_source (Path B only) ·
//   805 = quotes (Path B only) · 806 = 889 · 807 = abstract · 808 = dd2579 ·
//   809 = af_form9 · 810 = ecars · 811 = tax_exempt · 812 = receipt ·
//   813 = iuid.
// ===========================================================================

// ---------------------------------------------------------------------------
// Stage-key order + code mapping (TS mirror of `gpc_decision_rules.py`).
// ---------------------------------------------------------------------------

/**
 * Canonical ORDER of the post-Stage-3 per-form stages — mirrors the backend
 * `GPC_FORM_STAGE_ORDER`. Order is load-bearing: it is the order the dynamic
 * stepper renders the applicable subset after Product Discovery.
 */
export type GpcFormStageKey =
  | 'naics_psc'
  | 'mandatory_source'
  | 'quotes'
  | '889'
  | 'abstract'
  | 'dd2579'
  | 'af_form9'
  | 'ecars'
  | 'tax_exempt'
  | 'receipt'
  | 'iuid';

export const GPC_FORM_STAGE_ORDER: GpcFormStageKey[] = [
  'naics_psc',
  'mandatory_source',
  'quotes',
  '889',
  'abstract',
  'dd2579',
  'af_form9',
  'ecars',
  'tax_exempt',
  'receipt',
  'iuid',
];

/**
 * Stage-key → backend 80x code (mirrors `GPC_FORM_STAGE_CODE`). CLEAN CONTIGUOUS
 * scheme (2026-06-24): naics_psc (803) runs on both paths; award-vendor
 * designation + SAM enrichment now happen inside the Mandatory Source stage
 * (804). mandatory_source (804) + quotes (805) are Path-B-only; the existing
 * per-form stages renumber to 806..813.
 */
export const GPC_FORM_STAGE_CODE: Record<GpcFormStageKey, number> = {
  naics_psc: 803,
  mandatory_source: 804,
  quotes: 805,
  '889': 806,
  abstract: 807,
  dd2579: 808,
  af_form9: 809,
  ecars: 810,
  tax_exempt: 811,
  receipt: 812,
  iuid: 813,
};

/** Map a per-form stage key to its backend 80x code. */
export function formStageCode(key: GpcFormStageKey): number {
  return GPC_FORM_STAGE_CODE[key];
}

/**
 * Count the number of DISTINCT GSA Schedule / contract numbers among `products`.
 *
 * This is the value that gates AF Form 9 on Path B (FY26 deck Slide 42: the ≥3
 * rule requires 3 vendors each on a DIFFERENT GSA Schedule number — "no
 * exceptions"). It must count distinct schedules, NOT product rows: three rows
 * sharing one schedule are a single source, while schedule-less rows each count
 * as their own source.
 *
 * Schedule key per product (first non-empty wins):
 *   `contract_number` → `specifications.contract_number` → `source_type`.
 * When none is present, the row gets a per-product fallback key
 * (`__noschedule_${id}`) so each schedule-less row counts individually.
 */
export function gpcDistinctSourceCount(products: Product[]): number {
  if (!Array.isArray(products) || products.length === 0) return 0;
  const keys = new Set<string>();
  products.forEach((p, i) => {
    const anyP = p as any;
    const fallbackId = p?.id ?? `gpc-product-${i}`;
    const schedule =
      (typeof anyP?.contract_number === 'string' && anyP.contract_number.trim()) ||
      (typeof anyP?.specifications?.contract_number === 'string' &&
        anyP.specifications.contract_number.trim()) ||
      (typeof anyP?.source_type === 'string' && anyP.source_type.trim()) ||
      '';
    keys.add(schedule ? `sched_${schedule}` : `__noschedule_${fallbackId}`);
  });
  return keys.size;
}

// ---------------------------------------------------------------------------
// NAICS/PSC Selection (803) + Vendor Discovery (805) — payload types + mappers.
// Snake_case keys mirror the backend Pydantic models (gpc_data.py::GpcVendor)
// and the naics-psc/assign response shape (gpc_orchestrator.py::run_gpc_naics_psc).
// ---------------------------------------------------------------------------

/** A ranked PSC candidate from `POST /api/gpc/naics-psc/assign`. */
export interface GpcPscCandidate {
  code: string;
  description: string;
}

/** A ranked NAICS candidate from `POST /api/gpc/naics-psc/assign`. */
export interface GpcNaicsCandidate {
  code: string;
  title: string;
}

/**
 * NAICS/PSC selection payload (stage 803). `psc`/`naics`/`size_standard` are the
 * confirmed single winners; the `*_candidates` arrays are the ranked options the
 * cardholder confirms/overrides from. `size_standard_effective_date` surfaces the
 * `size_standards.json` table date (open-question #2) when the backend provides it.
 */
export interface GpcNaicsPsc {
  psc: string;
  naics: string;
  size_standard: string;
  size_standard_effective_date: string | null;
  psc_candidates: GpcPscCandidate[];
  naics_candidates: GpcNaicsCandidate[];
}

/**
 * The single award vendor (stage 805). Mirrors `gpc_data.py::GpcVendor`.
 * `is_small_business` is the REAL `vendor_is_small` derived from SAM's per-NAICS
 * small flag — `null` means unknown (SAM unavailable); the UI then prompts the
 * cardholder to confirm. NEVER defaults to `true`.
 */
export interface GpcVendor {
  legal_name: string;
  address: string;
  cage: string;
  uei: string;
  gsa_contract_number: string;
  naics: string;
  psc: string;
  size_standard: string;
  /** Socio-economic / set-aside codes from SAM, rendered as candidate chips. */
  business_types?: string[];
  website?: string | null;
  email?: string | null;
  phone?: string | null;
  poc_name?: string | null;
  is_small_business: boolean | null;
  /** Total historical award $ for this vendor (MR award-history engine); null when unknown. */
  award_total?: number | null;
}

export function mapGpcPscCandidate(data: any): GpcPscCandidate {
  const safe = data ?? {};
  return {
    code: safe.code ?? '',
    description: safe.description ?? '',
  };
}

export function mapGpcNaicsCandidate(data: any): GpcNaicsCandidate {
  const safe = data ?? {};
  return {
    code: safe.code ?? '',
    title: safe.title ?? '',
  };
}

export function mapGpcNaicsPsc(data: any): GpcNaicsPsc {
  const safe = data ?? {};
  return {
    psc: safe.psc ?? '',
    naics: safe.naics ?? '',
    size_standard: safe.size_standard ?? '',
    size_standard_effective_date: safe.size_standard_effective_date ?? null,
    psc_candidates: Array.isArray(safe.psc_candidates)
      ? safe.psc_candidates.map(mapGpcPscCandidate)
      : [],
    naics_candidates: Array.isArray(safe.naics_candidates)
      ? safe.naics_candidates.map(mapGpcNaicsCandidate)
      : [],
  };
}

export function mapGpcVendor(data: any): GpcVendor {
  const safe = data ?? {};
  return {
    legal_name: safe.legal_name ?? '',
    address: safe.address ?? '',
    cage: safe.cage ?? '',
    uei: safe.uei ?? '',
    gsa_contract_number: safe.gsa_contract_number ?? '',
    naics: safe.naics ?? '',
    psc: safe.psc ?? '',
    size_standard: safe.size_standard ?? '',
    business_types: Array.isArray(safe.business_types)
      ? safe.business_types.filter((bt: any): bt is string => typeof bt === 'string')
      : [],
    website: safe.website ?? '',
    email: safe.email ?? '',
    phone: safe.phone ?? '',
    poc_name: safe.poc_name ?? '',
    is_small_business:
      safe.is_small_business === true || safe.is_small_business === false
        ? safe.is_small_business
        : null,
    award_total:
      typeof safe.award_total === 'number' && !Number.isNaN(safe.award_total)
        ? safe.award_total
        : null,
  };
}

/**
 * Assign/rank PSC + NAICS candidates and resolve the size standard for a buy
 * (stage 803). POST `/api/gpc/naics-psc/assign`.
 */
export async function assignGpcNaicsPsc(
  flowId: string,
  requirementText: string,
  products?: any[],
): Promise<GpcNaicsPsc> {
  const response = await apiCall<any>('/api/gpc/naics-psc/assign', 'POST', {
    flow_id: flowId,
    requirement_text: requirementText,
    ...(products !== undefined ? { products } : {}),
  });
  if (!response.success) {
    throw buildContractError(response);
  }
  return mapGpcNaicsPsc(response.data);
}

/**
 * Persist the confirmed NAICS/PSC selection (stage 803). POST
 * `/api/gpc/naics-psc/save`. The backend echoes the payload back unchanged and
 * seeds `decision.psc`/`.naics`/`.size_standard` onto the stage-802 output.
 */
export async function saveGpcNaicsPsc(
  flowId: string,
  naicsPsc: GpcNaicsPsc,
): Promise<GpcNaicsPsc> {
  const response = await apiCall<any>('/api/gpc/naics-psc/save', 'POST', {
    flow_id: flowId,
    naics_psc: naicsPsc,
  });
  if (!response.success) {
    throw buildContractError(response);
  }
  return mapGpcNaicsPsc(response.data);
}

/**
 * Generate the DD 2579 narrative prefill (Blocks 10/11) from the NAICS/PSC +
 * vendor + source hierarchy (agent-backed). POST `/api/gpc/dd2579/prefill`.
 * Returns the full `GpcDd2579` dump mapped via `mapGpcDd2579`.
 */
export async function generateGpcDd2579Prefill(
  flowId: string,
  opts?: {
    context?: string;
    requirementTitle?: string;
    psc?: string;
    naics?: string;
    sizeStandard?: string;
    vendor?: GpcVendor | null;
    sourceHierarchy?: string[];
    /** Force a fresh agent run, bypassing/overwriting the persisted prefill cache. */
    forceRecompute?: boolean;
  },
): Promise<GpcDd2579> {
  const response = await apiCall<any>('/api/gpc/dd2579/prefill', 'POST', {
    flow_id: flowId,
    context: opts?.context ?? '',
    requirement_title: opts?.requirementTitle ?? '',
    psc: opts?.psc ?? '',
    naics: opts?.naics ?? '',
    size_standard: opts?.sizeStandard ?? '',
    force_recompute: opts?.forceRecompute ?? false,
    ...(opts?.vendor !== undefined ? { vendor: opts.vendor } : {}),
    ...(opts?.sourceHierarchy !== undefined ? { source_hierarchy: opts.sourceHierarchy } : {}),
  });
  if (!response.success) {
    throw buildContractError(response);
  }
  return mapGpcDd2579(response.data);
}

// ---------------------------------------------------------------------------
// applicableGpcFormStages — TS mirror of `applicable_form_stages(...)`.
// Identical signature (camelCase args) + identical output. Used by App.tsx to
// build the dynamic stepper without a round-trip. Keep in lockstep with
// `farchat/common/gpc_decision_rules.py::applicable_form_stages`.
// ---------------------------------------------------------------------------

/** DD-2579 dollar trigger (>$15K). Mirrors backend `DD2579_TRIGGER`. */
const DD2579_TRIGGER = 15_000.0;
/** IUID accountable-property unit-price trigger (≥$5K). */
const IUID_UNIT_PRICE_TRIGGER = 5_000.0;

/** Category keywords signalling a TAXABLE commercial buy. */
const TAXABLE_CATEGORY_KEYWORDS = [
  'supply', 'supplies', 'office supply', 'office supplies', 'equipment',
  'furniture', 'hardware', 'tool', 'tools', 'material', 'materials',
  'commercial', 'retail', 'product', 'goods', 'merchandise',
];
/** Channels where sales tax is not charged (exclude from taxable inference). */
const TAX_EXEMPT_CHANNEL_KEYWORDS = [
  'commissary', 'gsa global supply', 'global supply', 'fedmall',
];
/** Category keywords signalling highly-pilferable / accountable property. */
const PILFERABLE_KEYWORDS = [
  'laptop', 'computer', 'tablet', 'phone', 'smartphone', 'cell phone',
  'camera', 'weapon', 'firearm', 'small arms', 'ammunition', 'drone',
  'tool', 'power tool', 'pilferable', 'accountable property',
  'non-expendable', 'nonexpendable',
];

function isTaxableCategory(category: string): boolean {
  const cat = (category || '').toLowerCase();
  if (!cat.trim()) return false;
  if (TAX_EXEMPT_CHANNEL_KEYWORDS.some((kw) => cat.includes(kw))) return false;
  return TAXABLE_CATEGORY_KEYWORDS.some((kw) => cat.includes(kw));
}

function isPilferableCategory(category: string): boolean {
  const cat = (category || '').toLowerCase();
  if (!cat.trim()) return false;
  return PILFERABLE_KEYWORDS.some((kw) => cat.includes(kw));
}

/** Args for the applicable-stages decision (camelCase mirror of the backend kwargs). */
export interface ApplicableGpcFormStagesArgs {
  sourcingPath: GpcSourcingPath | null;
  dollarAmount: number | null;
  unitPrice: number | null;
  buyType: GpcBuyParams['buy_type'];
  sourceCount: number;
  vendorIsSmall: boolean;
  category: string;
  pilferable?: boolean;
  /** Explicit taxable signal; `null`/`undefined` => infer from category. */
  taxable?: boolean | null;
}

/**
 * Return the ORDERED subset of `GPC_FORM_STAGE_ORDER` that applies. Pure /
 * deterministic — never throws, never calls the network. Exact mirror of the
 * backend `applicable_form_stages`.
 */
export function applicableGpcFormStages(
  args: ApplicableGpcFormStagesArgs,
): GpcFormStageKey[] {
  const {
    sourcingPath,
    unitPrice,
    category,
    pilferable = false,
    taxable = null,
  } = args;

  const isPathB = sourcingPath === 'mandatory_source';
  // ALWAYS-on: naics_psc (803) runs on BOTH paths. Award-vendor designation +
  // SAM small-business enrichment now happen INSIDE the Mandatory Source stage
  // (804), so there is no longer a standalone vendor form stage.
  const triggered = new Set<GpcFormStageKey>([
    'naics_psc', '889', 'abstract', 'receipt',
  ]);

  // The dedicated mandatory-source determination + scoped search (804) — Path B
  // ONLY.
  if (isPathB) {
    triggered.add('mandatory_source');
  }

  // Path-B-only ≥3-quote / competition capture (806). Path A's open-market price
  // history is already captured by the price auto-search.
  if (isPathB) {
    triggered.add('quotes');
  }

  // DD 2579 and AF Form 9 are ALWAYS enabled on Path B (mandatory_source) so the
  // cardholder can review them. Whether they are strictly REQUIRED for the buy
  // (which drives an informational note inside each stage) is determined by
  // `gpcStageRequirement`, NOT by applicability.
  if (isPathB) {
    triggered.add('dd2579');
  }

  if (isPathB) {
    triggered.add('af_form9');
  }

  if (isPathB) {
    triggered.add('ecars');
  }

  const isTaxable =
    taxable !== null && taxable !== undefined ? taxable : isTaxableCategory(category);
  if (isTaxable) {
    triggered.add('tax_exempt');
  }

  const iuidByPrice =
    unitPrice !== null && unitPrice !== undefined && unitPrice >= IUID_UNIT_PRICE_TRIGGER;
  if (iuidByPrice || pilferable || isPilferableCategory(category)) {
    triggered.add('iuid');
  }

  return GPC_FORM_STAGE_ORDER.filter((key) => triggered.has(key));
}

/**
 * Whether a form stage is strictly REQUIRED for the active buy, plus (when it is
 * applicable-but-not-required) the user-facing reason why it is optional.
 *
 * `af_form9` and `dd2579` are ALWAYS enabled on Path B (see
 * `applicableGpcFormStages`), but each is only strictly REQUIRED under its
 * specific trigger. When applicable-but-not-required, `reason` carries an
 * informational note rendered inside the stage; when required, `reason` is
 * `null` (no note). For every other key this helper just mirrors
 * applicability with a `null` reason (it is only meaningfully consumed by the
 * two forms above).
 */
export interface GpcStageRequirement {
  required: boolean;
  reason: string | null;
}

export function gpcStageRequirement(
  key: GpcFormStageKey,
  args: ApplicableGpcFormStagesArgs,
): GpcStageRequirement {
  const { sourcingPath, dollarAmount, sourceCount, vendorIsSmall } = args;
  const isPathB = sourcingPath === 'mandatory_source';

  if (key === 'af_form9') {
    const required = isPathB && sourceCount < 3;
    if (required) {
      return { required: true, reason: null };
    }
    // Applicable-but-not-required only happens on Path B (≥3 sources).
    if (isPathB) {
      return {
        required: false,
        reason:
          'Not required: 3 or more sources were obtained (sole-source ' +
          'justification applies only when fewer than 3 sources are available).',
      };
    }
    return { required: false, reason: null };
  }

  if (key === 'dd2579') {
    const overThreshold =
      dollarAmount !== null &&
      dollarAmount !== undefined &&
      dollarAmount > DD2579_TRIGGER;
    const required = isPathB && overThreshold && !vendorIsSmall;
    if (required) {
      return { required: true, reason: null };
    }
    if (isPathB) {
      // Choose the unmet gate; prefer the dollar reason when both fail.
      if (!overThreshold) {
        return {
          required: false,
          reason: 'Not required: award is at or below the $15,000 threshold.',
        };
      }
      return {
        required: false,
        reason: 'Not required: the awarded vendor is a small business.',
      };
    }
    return { required: false, reason: null };
  }

  // Any other key: mirror applicability, no note.
  return {
    required: applicableGpcFormStages(args).includes(key),
    reason: null,
  };
}

/**
 * The FULL, FIXED ordered list of every GPC per-form stage key. This is just
 * `GPC_FORM_STAGE_ORDER` re-exposed as a function for the stepper, which now
 * ALWAYS renders every form stage (inapplicable ones greyed in place) rather
 * than the filtered applicable subset. Order is load-bearing and never changes
 * with applicability.
 */
export function allGpcFormStages(): GpcFormStageKey[] {
  return [...GPC_FORM_STAGE_ORDER];
}

// ---------------------------------------------------------------------------
// Stepper navigation over the FULL form-stage list (with greyed/disabled stages).
//
// The GPC stepper renders the 3 base stages (UI indices 0..2) followed by ALL
// 11 form stages (UI indices 3..13) in their canonical order. A form stage is
// ENABLED iff its key is in the applicable set (`applicableGpcFormStages`);
// otherwise it is DISABLED (greyed, non-clickable). Navigation must SKIP the
// disabled stages. These helpers centralize that index math so App.tsx and the
// tests share one source of truth.
// ---------------------------------------------------------------------------

/** First UI index of the form-stage portion of the GPC stepper (after base 3). */
export const GPC_FORM_STAGE_BASE_OFFSET = 3;

/**
 * Map a full-list UI index (0..13) to its applicability:
 *   - indices 0..2 (base stages) are ALWAYS enabled.
 *   - indices >= 3 map to `GPC_FORM_STAGE_ORDER[index - 3]`; enabled iff that
 *     key is in `applicableSet`.
 */
export function isGpcStageEnabled(
  index: number,
  applicableSet: Set<GpcFormStageKey> | GpcFormStageKey[],
): boolean {
  if (index < GPC_FORM_STAGE_BASE_OFFSET) return true;
  const key = GPC_FORM_STAGE_ORDER[index - GPC_FORM_STAGE_BASE_OFFSET];
  if (!key) return false;
  const set = Array.isArray(applicableSet) ? new Set(applicableSet) : applicableSet;
  return set.has(key);
}

/**
 * The DISABLED full-list UI indices (form stages whose key is not applicable).
 * Base stages are never disabled. Returned sorted ascending.
 */
export function gpcDisabledStageIndices(
  applicableSet: Set<GpcFormStageKey> | GpcFormStageKey[],
): number[] {
  const set = Array.isArray(applicableSet) ? new Set(applicableSet) : applicableSet;
  const out: number[] = [];
  for (let i = 0; i < GPC_FORM_STAGE_ORDER.length; i += 1) {
    if (!set.has(GPC_FORM_STAGE_ORDER[i])) {
      out.push(GPC_FORM_STAGE_BASE_OFFSET + i);
    }
  }
  return out;
}

/**
 * The SPECIFIC, user-facing reason a GPC form stage is inapplicable for the
 * active buy — mirrors the real gates in `applicableGpcFormStages`. Returns
 * `null` when the stage IS applicable (so it should not be greyed) or when no
 * specific reason applies. Used to populate per-stage tooltips in the stepper.
 */
export function gpcDisabledStageReason(
  key: GpcFormStageKey,
  args: ApplicableGpcFormStagesArgs,
): string | null {
  // If the stage is actually applicable, there is no "disabled" reason.
  if (applicableGpcFormStages(args).includes(key)) {
    return null;
  }

  const { sourcingPath, dollarAmount } = args;
  const isPathB = sourcingPath === 'mandatory_source';

  switch (key) {
    case 'mandatory_source':
    case 'quotes':
    case 'ecars':
      return 'Only for over-threshold (Path B) purchases.';

    case 'dd2579':
      // Pick the failing gate, in the order they are evaluated.
      if (!isPathB) {
        return 'Only for Path B (over-threshold) purchases.';
      }
      if (
        dollarAmount === null ||
        dollarAmount === undefined ||
        dollarAmount <= DD2579_TRIGGER
      ) {
        return 'Only for purchases over $15,000.';
      }
      // Path B + > $15K but vendor is small.
      return 'Only when the awarded vendor is not a small business.';

    case 'af_form9':
      if (!isPathB) {
        return 'Only for Path B purchases.';
      }
      // Path B but >= 3 sources found.
      return 'Only when fewer than 3 sources are found (≥3 found).';

    case 'tax_exempt':
      return 'Only for taxable commodity categories.';

    case 'iuid':
      return 'Only for items with a unit price ≥ $5,000 or pilferable/serialized items.';

    default:
      // Always-on stages (naics_psc, 889, abstract, receipt) never
      // reach here because they are always applicable.
      return null;
  }
}

/**
 * Build a `{ stepperIndex → reason }` map for every DISABLED form stage of the
 * active buy. Pairs with `gpcDisabledStageIndices` (same indices) and feeds
 * `StageWrapper`'s `disabledStageReasons` prop so each greyed stage shows its
 * own specific tooltip.
 */
export function gpcDisabledStageReasonMap(
  args: ApplicableGpcFormStagesArgs,
): Record<number, string> {
  const applicable = new Set<GpcFormStageKey>(applicableGpcFormStages(args));
  const out: Record<number, string> = {};
  for (let i = 0; i < GPC_FORM_STAGE_ORDER.length; i += 1) {
    const key = GPC_FORM_STAGE_ORDER[i];
    if (!applicable.has(key)) {
      const reason = gpcDisabledStageReason(key, args);
      if (reason) {
        out[GPC_FORM_STAGE_BASE_OFFSET + i] = reason;
      }
    }
  }
  return out;
}

/**
 * Total length of the GPC stepper (always constant: 3 base + all form stages).
 */
export function gpcTotalStageCount(): number {
  return GPC_FORM_STAGE_BASE_OFFSET + GPC_FORM_STAGE_ORDER.length;
}

/**
 * Next ENABLED stage index AT OR AFTER `from`, skipping disabled stages.
 * Returns `null` when there is no enabled stage at/after `from` (i.e. `from` is
 * already past the last enabled stage — the workflow is complete).
 */
export function nextEnabledGpcStage(
  from: number,
  applicableSet: Set<GpcFormStageKey> | GpcFormStageKey[],
): number | null {
  const total = gpcTotalStageCount();
  for (let i = Math.max(0, from); i < total; i += 1) {
    if (isGpcStageEnabled(i, applicableSet)) return i;
  }
  return null;
}

/**
 * Previous ENABLED stage index AT OR BEFORE `from`, skipping disabled stages.
 * Returns `null` when none exists.
 */
export function prevEnabledGpcStage(
  from: number,
  applicableSet: Set<GpcFormStageKey> | GpcFormStageKey[],
): number | null {
  const total = gpcTotalStageCount();
  for (let i = Math.min(from, total - 1); i >= 0; i -= 1) {
    if (isGpcStageEnabled(i, applicableSet)) return i;
  }
  return null;
}

/**
 * The HIGHEST enabled stage index reachable given a max-completed index
 * `maxReached`. Used so the stepper's reachable gate lands on an enabled stage.
 */
export function lastEnabledGpcStageAtOrBelow(
  maxReached: number,
  applicableSet: Set<GpcFormStageKey> | GpcFormStageKey[],
): number {
  const total = gpcTotalStageCount();
  const cap = Math.min(maxReached, total - 1);
  const prev = prevEnabledGpcStage(cap, applicableSet);
  return prev ?? 0;
}

/** Server echo of the applicable-stages decision (the authoritative source). */
export async function applicableGpcFormStagesRemote(
  args: ApplicableGpcFormStagesArgs & { flowId?: string | null },
): Promise<{ stages: GpcFormStageKey[]; codes: number[] }> {
  const response = await apiCall<{ stages?: string[]; codes?: number[] }>(
    '/api/gpc/forms/applicable-stages',
    'POST',
    {
      flow_id: args.flowId ?? null,
      sourcing_path: args.sourcingPath ?? null,
      dollar_amount: args.dollarAmount ?? null,
      unit_price: args.unitPrice ?? null,
      buy_type: args.buyType ?? null,
      source_count: args.sourceCount ?? 0,
      vendor_is_small: args.vendorIsSmall ?? true,
      category: args.category ?? '',
      pilferable: args.pilferable ?? false,
      taxable: args.taxable ?? null,
    },
  );

  if (!response.success) {
    throw buildContractError(response);
  }
  const data = (response.data as { stages?: string[]; codes?: number[] }) ?? {};
  return {
    stages: Array.isArray(data.stages) ? (data.stages as GpcFormStageKey[]) : [],
    codes: Array.isArray(data.codes) ? data.codes : [],
  };
}

// ---------------------------------------------------------------------------
// S4 (804) — 889 Representation.
// ---------------------------------------------------------------------------

/** 889 covered-telecom representation — mirrors backend `Gpc889Representation`. */
export interface Gpc889Representation {
  vendor_name: string;
  method: 'caamp_bot' | 'gsa_889_easy_search' | 'sam_certs_reps' | 'other' | null;
  represents_no_covered_telecom: boolean | null;
  exempt: boolean;
  exempt_reason: string;
  confirmed_on_file: boolean;
}

/** Map the backend snake_case 889 dict into `Gpc889Representation`. */
export function mapGpc889(data: any): Gpc889Representation {
  const safe = data ?? {};
  return {
    vendor_name: safe.vendor_name ?? '',
    method: safe.method ?? null,
    represents_no_covered_telecom: safe.represents_no_covered_telecom ?? null,
    exempt: !!safe.exempt,
    exempt_reason: safe.exempt_reason ?? '',
    confirmed_on_file: !!safe.confirmed_on_file,
  };
}

/**
 * Result of an on-demand 889 representation lookup — mirrors the backend
 * `POST /api/gpc/889/lookup` response `data`. Non-persisting; the user reviews
 * the result, then Confirm & Continue saves via the existing form save.
 */
export interface Gpc889LookupResult {
  status: 'compliant' | 'non_compliant' | 'no_representation' | 'unknown';
  represents_no_covered_telecom: boolean | null;
  method: 'caamp_bot' | 'gsa_889_easy_search' | 'sam_certs_reps' | 'other' | null;
  uei: string | null;
  checked_at: string | null;
}

/**
 * Look up a vendor's Section 889 representation on demand
 * (`POST /api/gpc/889/lookup`). Non-persisting. By default the backend uses the
 * supplied `vendorName`; `uei`/`cage` refine the match (important on Path A,
 * below the micro-purchase threshold, where no vendor is auto-resolved).
 */
export async function lookupGpc889Representation(
  flowId: string,
  uei?: string,
  cage?: string,
  vendorName?: string,
): Promise<Gpc889LookupResult> {
  const response = await apiCall<any>('/api/gpc/889/lookup', 'POST', {
    flow_id: flowId,
    uei: uei || undefined,
    cage: cage || undefined,
    vendor_name: vendorName || undefined,
  });
  if (!response.success) {
    throw buildContractError(response);
  }
  return response.data as Gpc889LookupResult;
}

/** Persist the 889 representation (`POST /api/gpc/forms/889`, stage 804). */
export async function saveGpc889Representation(
  flowId: string,
  rep: Gpc889Representation,
  evidence?: Record<string, unknown>,
): Promise<Gpc889Representation> {
  const response = await apiCall<any>('/api/gpc/forms/889', 'POST', {
    flow_id: flowId,
    representation: rep,
    ...(evidence && Object.keys(evidence).length ? { evidence } : {}),
  });
  if (!response.success) {
    throw buildContractError(response);
  }
  return mapGpc889(response.data);
}

// ---------------------------------------------------------------------------
// S5 (805) — GPC Abstract of Proposals (+ optional pre-fill).
// ---------------------------------------------------------------------------

/** A single §D line item — mirrors backend `GpcAbstractLineItem`. */
export interface GpcAbstractLineItem {
  description: string;
  quantity: number | null;
  unit_price: number | null;
  extended_price: number | null;
}

/** GPC Abstract of Proposals — mirrors backend `GpcAbstract`. */
export interface GpcAbstract {
  section_a_item_description: string;
  section_b_priority_of_sources: string[];
  section_c_vendor_name: string;
  section_c_vendor_is_small: boolean | null;
  section_d_line_items: GpcAbstractLineItem[];
  award_total: number | null;
  section_e_price_reasonableness: string;
  section_e_cardholder_signature: string;
  section_f_ao_control_number: string;
  section_f_authorization_attachments: string[];
  section_g_delivery_terms: string;
  section_h_property_custodian_sig: string;
  section_h_uslm_sig: string;
  section_h_beco_email: string;
  section_h_tac_radio_email: string;
}

/** Map a backend snake_case line-item dict into `GpcAbstractLineItem`. */
export function mapGpcAbstractLineItem(data: any): GpcAbstractLineItem {
  const safe = data ?? {};
  return {
    description: safe.description ?? '',
    quantity: safe.quantity ?? null,
    unit_price: safe.unit_price ?? null,
    extended_price: safe.extended_price ?? null,
  };
}

/** Map the backend snake_case abstract dict into `GpcAbstract`. */
export function mapGpcAbstract(data: any): GpcAbstract {
  const safe = data ?? {};
  return {
    section_a_item_description: safe.section_a_item_description ?? '',
    section_b_priority_of_sources: Array.isArray(safe.section_b_priority_of_sources)
      ? safe.section_b_priority_of_sources
      : [],
    section_c_vendor_name: safe.section_c_vendor_name ?? '',
    section_c_vendor_is_small: safe.section_c_vendor_is_small ?? null,
    section_d_line_items: Array.isArray(safe.section_d_line_items)
      ? safe.section_d_line_items.map(mapGpcAbstractLineItem)
      : [],
    award_total: safe.award_total ?? null,
    section_e_price_reasonableness: safe.section_e_price_reasonableness ?? '',
    section_e_cardholder_signature: safe.section_e_cardholder_signature ?? '',
    section_f_ao_control_number: safe.section_f_ao_control_number ?? '',
    section_f_authorization_attachments: Array.isArray(safe.section_f_authorization_attachments)
      ? safe.section_f_authorization_attachments
      : [],
    section_g_delivery_terms: safe.section_g_delivery_terms ?? '',
    section_h_property_custodian_sig: safe.section_h_property_custodian_sig ?? '',
    section_h_uslm_sig: safe.section_h_uslm_sig ?? '',
    section_h_beco_email: safe.section_h_beco_email ?? '',
    section_h_tac_radio_email: safe.section_h_tac_radio_email ?? '',
  };
}

/**
 * Choose the Abstract record that should hydrate a reopened flow. A forced
 * prefill is persisted as a complete canonical result in `prefill_cache` next
 * to the older saved draft, and must win until a later explicit form save.
 */
export function restoreGpcAbstractFromStageOutput(
  output: {
    abstract?: unknown;
    prefill_cache?: { result?: unknown } | null;
  } | null | undefined,
): GpcAbstract | null {
  const candidate = output?.prefill_cache?.result ?? output?.abstract;
  return candidate && typeof candidate === 'object' ? mapGpcAbstract(candidate) : null;
}

/**
 * Optional LLM pre-fill of the GPC Abstract §A/§B/§D/§E
 * (`POST /api/gpc/abstract/prefill`). Degrades to an empty-but-valid abstract on
 * any agent failure. NOT persisted by the prefill call — the user edits then
 * saves via `saveGpcAbstract`.
 */
export async function prefillGpcAbstract(
  flowId: string,
  requirementText: string,
  opts?: {
    salientCharacteristics?: string;
    sourceHierarchy?: string[];
    priceQuotes?: GpcPriceQuote[];
    sourcingPath?: GpcSourcingPath | null;
    /** Force a fresh agent run, bypassing/overwriting the persisted prefill cache. */
    forceRecompute?: boolean;
  },
): Promise<GpcAbstract> {
  const response = await apiCall<any>('/api/gpc/abstract/prefill', 'POST', {
    flow_id: flowId,
    requirement_text: requirementText,
    salient_characteristics: opts?.salientCharacteristics ?? '',
    force_recompute: opts?.forceRecompute ?? false,
    ...(opts?.sourceHierarchy !== undefined ? { source_hierarchy: opts.sourceHierarchy } : {}),
    ...(opts?.priceQuotes !== undefined ? { price_quotes: opts.priceQuotes } : {}),
    ...(opts?.sourcingPath != null ? { sourcing_path: opts.sourcingPath } : {}),
  });
  if (!response.success) {
    throw buildContractError(response);
  }
  return mapGpcAbstract(response.data);
}

/** Persist the GPC Abstract (`POST /api/gpc/abstract/save`, stage 805). */
export async function saveGpcAbstract(
  flowId: string,
  abstract: GpcAbstract,
  evidence?: Record<string, unknown>,
): Promise<GpcAbstract> {
  const response = await apiCall<any>('/api/gpc/abstract/save', 'POST', {
    flow_id: flowId,
    abstract,
    ...(evidence && Object.keys(evidence).length ? { evidence } : {}),
  });
  if (!response.success) {
    throw buildContractError(response);
  }
  return mapGpcAbstract(response.data);
}

// ---------------------------------------------------------------------------
// S6 (806) — DD/DAF 2579.
// ---------------------------------------------------------------------------

/**
 * DD Form 2579 Small Business Coordination Record — mirrors backend
 * `GpcDd2579` (FULL official-form text fidelity, Blocks 1–18). Signature IMAGE
 * fields are omitted; typed name / email / date / remarks / concur fields are
 * kept. The legacy `vendor_is_small` / `sbo_name` / `coordinated` /
 * `coordinated_at` / `notes` fields are KEPT (Block-16 canonical mappings).
 */
export interface GpcDd2579 {
  // Blocks 1–8 — acquisition context (cardholder / requester)
  control_no: string;
  purchase_request_no: string;
  total_estimated_value: string;
  piid: string;
  piid_2: string;
  supplementary_piid: string;
  contracting_officer_name: string;
  dodaac: string;
  office_symbol: string;
  contracting_officer_email: string;
  contracting_officer_phone: string;
  item_or_service_description: string;
  psc: string;
  naics: string;
  size_standard: string;
  period_of_performance: string;
  // Block 9 — purpose of coordination (cardholder / requester)
  purpose: 'initial' | 'withdrawal' | 'change' | null;
  // Block 10 — recommendation (Contracting Officer — local CONS)
  set_aside_small_business: boolean;
  set_aside_partial_percent: string;
  set_aside_8a: boolean;
  set_aside_hubzone: boolean;
  set_aside_sdvosb: boolean;
  set_aside_edwosb: boolean;
  set_aside_wosb: boolean;
  set_aside_other: boolean;
  set_aside_other_authority: string;
  other_than_full_open_reason: string;
  full_and_open: boolean | null;
  multiple_award_reserves: string;
  // Blocks 11–13 — market research / acq plan (Contracting Officer)
  market_research_done: boolean | null;
  synopsis_required: boolean | null;
  progress_payments: boolean | null;
  progress_payments_far_exception: string;
  consolidated: boolean | null;
  consolidated_note: string;
  bundled: boolean | null;
  bundled_note: string;
  subcontracting_plan_required: boolean | null;
  // Block 14 — acquisition history (Contracting Officer)
  is_new_requirement: boolean | null;
  prior_set_aside_small_business: boolean;
  prior_set_aside_8a: boolean;
  prior_set_aside_hubzone: boolean;
  prior_set_aside_sdvosb: boolean;
  prior_set_aside_edwosb: boolean;
  prior_set_aside_wosb: boolean;
  prior_set_aside_other: boolean;
  prior_consolidated: boolean | null;
  prior_bundled: boolean | null;
  prior_award_details: string;
  // Block 15 — Contracting Officer signature block
  co_name: string;
  co_email: string;
  co_date: string;
  co_remarks: string;
  // Block 16 — Installation Small Business Office (SBP/SBS); legacy canonical:
  // sbo_name = name, coordinated = concur, coordinated_at = date.
  sbp_concur: boolean | null;
  sbp_email: string;
  sbp_remarks: string;
  date_provided_to_sba: string;
  // Block 17 — SBA Procurement Center Representative (PCR)
  sba_pcr_name: string;
  sba_pcr_email: string;
  sba_pcr_date: string;
  sba_pcr_concur: boolean | null;
  sba_pcr_remarks: string;
  /** Actual native representative applicability facts; absence preserves legacy forms. */
  sba_pcr_review_applicability?: {
    status: 'requires_review' | 'documented_exclusion' | 'pending_unknown';
    exclusion_code?: 'foreign_military_sales' | 'humanitarian_civic' | 'contingency' | 'foreign_agreement' | 'awarded_and_performed_outside_us' | null;
    activity_review_requested: boolean | null;
  };
  // Block 18 — Contracting Officer review
  co_review_name: string;
  co_review_email: string;
  co_review_date: string;
  co_review_concur: boolean | null;
  co_review_remarks: string;
  // Legacy / canonical Block-16 + buy context (BACKWARD COMPATIBLE)
  vendor_is_small: boolean | null;
  sbo_name: string;
  coordinated: boolean;
  coordinated_at: string | null;
  notes: string;
}

const _bool = (v: any): boolean => !!v;
const _str = (v: any): string => (v ?? '') as string;
const _triBool = (v: any): boolean | null => (v === true || v === false ? v : null);

/** Map the backend snake_case DD-2579 dict into `GpcDd2579`. */
export function mapGpcDd2579(data: any): GpcDd2579 {
  const safe = data ?? {};
  return {
    // Blocks 1–8
    control_no: _str(safe.control_no),
    purchase_request_no: _str(safe.purchase_request_no),
    total_estimated_value: _str(safe.total_estimated_value),
    piid: _str(safe.piid),
    piid_2: _str(safe.piid_2),
    supplementary_piid: _str(safe.supplementary_piid),
    contracting_officer_name: _str(safe.contracting_officer_name),
    dodaac: _str(safe.dodaac),
    office_symbol: _str(safe.office_symbol),
    contracting_officer_email: _str(safe.contracting_officer_email),
    contracting_officer_phone: _str(safe.contracting_officer_phone),
    item_or_service_description: _str(safe.item_or_service_description),
    psc: _str(safe.psc),
    naics: _str(safe.naics),
    size_standard: _str(safe.size_standard),
    period_of_performance: _str(safe.period_of_performance),
    // Block 9
    purpose: safe.purpose ?? null,
    // Block 10
    set_aside_small_business: _bool(safe.set_aside_small_business),
    set_aside_partial_percent: _str(safe.set_aside_partial_percent),
    set_aside_8a: _bool(safe.set_aside_8a),
    set_aside_hubzone: _bool(safe.set_aside_hubzone),
    set_aside_sdvosb: _bool(safe.set_aside_sdvosb),
    set_aside_edwosb: _bool(safe.set_aside_edwosb),
    set_aside_wosb: _bool(safe.set_aside_wosb),
    set_aside_other: _bool(safe.set_aside_other),
    set_aside_other_authority: _str(safe.set_aside_other_authority),
    other_than_full_open_reason: _str(safe.other_than_full_open_reason),
    full_and_open: _triBool(safe.full_and_open),
    multiple_award_reserves: _str(safe.multiple_award_reserves),
    // Blocks 11–13
    market_research_done: _triBool(safe.market_research_done),
    synopsis_required: _triBool(safe.synopsis_required),
    progress_payments: _triBool(safe.progress_payments),
    progress_payments_far_exception: _str(safe.progress_payments_far_exception),
    consolidated: _triBool(safe.consolidated),
    consolidated_note: _str(safe.consolidated_note),
    bundled: _triBool(safe.bundled),
    bundled_note: _str(safe.bundled_note),
    subcontracting_plan_required: _triBool(safe.subcontracting_plan_required),
    // Block 14
    is_new_requirement: _triBool(safe.is_new_requirement),
    prior_set_aside_small_business: _bool(safe.prior_set_aside_small_business),
    prior_set_aside_8a: _bool(safe.prior_set_aside_8a),
    prior_set_aside_hubzone: _bool(safe.prior_set_aside_hubzone),
    prior_set_aside_sdvosb: _bool(safe.prior_set_aside_sdvosb),
    prior_set_aside_edwosb: _bool(safe.prior_set_aside_edwosb),
    prior_set_aside_wosb: _bool(safe.prior_set_aside_wosb),
    prior_set_aside_other: _bool(safe.prior_set_aside_other),
    prior_consolidated: _triBool(safe.prior_consolidated),
    prior_bundled: _triBool(safe.prior_bundled),
    prior_award_details: _str(safe.prior_award_details),
    // Block 15
    co_name: _str(safe.co_name),
    co_email: _str(safe.co_email),
    co_date: _str(safe.co_date),
    co_remarks: _str(safe.co_remarks),
    // Block 16
    sbp_concur: _triBool(safe.sbp_concur),
    sbp_email: _str(safe.sbp_email),
    sbp_remarks: _str(safe.sbp_remarks),
    date_provided_to_sba: _str(safe.date_provided_to_sba),
    // Block 17
    sba_pcr_name: _str(safe.sba_pcr_name),
    sba_pcr_email: _str(safe.sba_pcr_email),
    sba_pcr_date: _str(safe.sba_pcr_date),
    sba_pcr_concur: _triBool(safe.sba_pcr_concur),
    sba_pcr_remarks: _str(safe.sba_pcr_remarks),
    ...(safe.sba_pcr_review_applicability && typeof safe.sba_pcr_review_applicability === 'object'
      ? { sba_pcr_review_applicability: { ...safe.sba_pcr_review_applicability } } : {}),
    // Block 18
    co_review_name: _str(safe.co_review_name),
    co_review_email: _str(safe.co_review_email),
    co_review_date: _str(safe.co_review_date),
    co_review_concur: _triBool(safe.co_review_concur),
    co_review_remarks: _str(safe.co_review_remarks),
    // Legacy / canonical Block-16 + buy context
    vendor_is_small: safe.vendor_is_small ?? null,
    sbo_name: _str(safe.sbo_name),
    coordinated: _bool(safe.coordinated),
    coordinated_at: safe.coordinated_at ?? null,
    notes: _str(safe.notes),
  };
}

/**
 * Choose the record that should hydrate DD-2579 after returning to a flow.
 * A forced prefill intentionally writes its full canonical result alongside an
 * older saved form at `prefill_cache.result`; the fresh cache is authoritative
 * until the user explicitly saves a later form. Reading `dd2579` first would
 * resurrect values the user deliberately replaced with a fresh prefill.
 */
export function restoreGpcDd2579FromStageOutput(
  output: {
    dd2579?: unknown;
    prefill_cache?: { result?: unknown } | null;
  } | null | undefined,
): GpcDd2579 | null {
  const candidate = output?.prefill_cache?.result ?? output?.dd2579;
  return candidate && typeof candidate === 'object' ? mapGpcDd2579(candidate) : null;
}

/** Persist the DD/DAF 2579 (`POST /api/gpc/forms/dd2579`, stage 806). */
export async function saveGpcDd2579(
  flowId: string,
  dd2579: GpcDd2579,
  evidence?: Record<string, unknown>,
): Promise<GpcDd2579> {
  const response = await apiCall<any>('/api/gpc/forms/dd2579', 'POST', {
    flow_id: flowId,
    dd2579,
    ...(evidence && Object.keys(evidence).length ? { evidence } : {}),
  });
  if (!response.success) {
    throw buildContractError(response);
  }
  return mapGpcDd2579(response.data);
}

// ---------------------------------------------------------------------------
// S7 (807) — AF Form 9.
// ---------------------------------------------------------------------------

/** A single page-1 line-item row — mirrors backend `GpcAfForm9LineItem`. */
export interface GpcAfForm9LineItem {
  item_number: string;
  description: string;
  quantity: number | null;
  unit: string;
  estimated_unit_price: number | null;
  estimated_total_cost: number | null; // qty × unit price (validator computed)
}

/**
 * AF Form 9 (Request for Purchase, routes to local CONS) — mirrors backend
 * `GpcAfForm9` (FULL official-form text fidelity). Fields are grouped by WHO
 * fills them: page-1 header + line items + purpose + requester block
 * (cardholder); approver block (the Approving Official, whose duty title may be 'Resource Advisor'); the
 * page-2 certification + amount + accounting classification (Financial
 * Management — 23 CPTS/FMA). Signature IMAGE fields are omitted; typed
 * name/grade + date text fields are kept. The legacy
 * `ebuy_rfq_attempted` / `sources_found` / `submitted_to_23cons` /
 * `submitted_at` / `notes` workflow-context fields are KEPT (backward compatible).
 */
export interface GpcAfForm9 {
  // Page-1 header (cardholder / requester)
  purchase_request_no: string;
  installation: string;
  form_date: string;
  contracting_officer: string;
  class_code: string;
  routing_through: string;
  contract_po_do_no: string;
  from_rc_cc: string;
  purchased_for: string;
  delivery_not_later_than: string;
  // Page-1 line-item table (cardholder / requester)
  line_items: GpcAfForm9LineItem[];
  grand_total: number | null;
  // Purpose narrative (cardholder / requester)
  purpose: string;
  // Requester block (cardholder / requester) — signature omitted
  requester_name_grade: string;
  requester_org: string;
  requester_phone: string;
  requester_date: string;
  // Approver block (Approving Official)
  approver_name_grade: string;
  resource_advisor_org: string;
  resource_advisor_phone: string;
  approver_date: string;
  // Page-2 certification (Financial Management — 23 CPTS/FMA)
  certifying_official_name_grade: string;
  certifier_org: string;
  certifier_phone: string;
  certifier_date: string;
  funds_certification_statement: string;
  amount: number | null;
  document_number: string;
  accounting_classification: string;
  // Legacy workflow-context fields (BACKWARD COMPATIBLE)
  ebuy_rfq_attempted: boolean;
  sources_found: number | null;
  submitted_to_23cons: boolean;
  submitted_at: string | null;
  notes: string;
}

/** Map a backend snake_case AF-Form-9 line-item dict into `GpcAfForm9LineItem`. */
export function mapGpcAfForm9LineItem(data: any): GpcAfForm9LineItem {
  const safe = data ?? {};
  return {
    item_number: safe.item_number ?? '',
    description: safe.description ?? '',
    quantity: safe.quantity ?? null,
    unit: safe.unit ?? '',
    estimated_unit_price: safe.estimated_unit_price ?? null,
    estimated_total_cost: safe.estimated_total_cost ?? null,
  };
}

/** Map the backend snake_case AF-Form-9 dict into `GpcAfForm9`. */
export function mapGpcAfForm9(data: any): GpcAfForm9 {
  const safe = data ?? {};
  return {
    // Page-1 header
    purchase_request_no: safe.purchase_request_no ?? '',
    installation: safe.installation ?? '',
    form_date: safe.form_date ?? '',
    contracting_officer: safe.contracting_officer ?? '',
    class_code: safe.class_code ?? '',
    routing_through: safe.routing_through ?? '',
    contract_po_do_no: safe.contract_po_do_no ?? '',
    from_rc_cc: safe.from_rc_cc ?? '',
    purchased_for: safe.purchased_for ?? '',
    delivery_not_later_than: safe.delivery_not_later_than ?? '',
    // Line-item table
    line_items: Array.isArray(safe.line_items)
      ? safe.line_items.map(mapGpcAfForm9LineItem)
      : [],
    grand_total: safe.grand_total ?? null,
    // Purpose
    purpose: safe.purpose ?? '',
    // Requester block
    requester_name_grade: safe.requester_name_grade ?? '',
    requester_org: safe.requester_org ?? '',
    requester_phone: safe.requester_phone ?? '',
    requester_date: safe.requester_date ?? '',
    // Approver block
    approver_name_grade: safe.approver_name_grade ?? '',
    resource_advisor_org: safe.resource_advisor_org ?? '',
    resource_advisor_phone: safe.resource_advisor_phone ?? '',
    approver_date: safe.approver_date ?? '',
    // Page-2 certification
    certifying_official_name_grade: safe.certifying_official_name_grade ?? '',
    certifier_org: safe.certifier_org ?? '',
    certifier_phone: safe.certifier_phone ?? '',
    certifier_date: safe.certifier_date ?? '',
    funds_certification_statement: safe.funds_certification_statement ?? '',
    amount: safe.amount ?? null,
    document_number: safe.document_number ?? '',
    accounting_classification: safe.accounting_classification ?? '',
    // Legacy workflow-context fields
    ebuy_rfq_attempted: !!safe.ebuy_rfq_attempted,
    sources_found: safe.sources_found ?? null,
    submitted_to_23cons: !!safe.submitted_to_23cons,
    submitted_at: safe.submitted_at ?? null,
    notes: safe.notes ?? '',
  };
}

/** Persist the AF Form 9 (`POST /api/gpc/forms/af9`, stage 807). */
export async function saveGpcAfForm9(
  flowId: string,
  af9: GpcAfForm9,
  evidence?: Record<string, unknown>,
): Promise<GpcAfForm9> {
  const response = await apiCall<any>('/api/gpc/forms/af9', 'POST', {
    flow_id: flowId,
    af9,
    ...(evidence && Object.keys(evidence).length ? { evidence } : {}),
  });
  if (!response.success) {
    throw buildContractError(response);
  }
  return mapGpcAfForm9(response.data);
}

// ---------------------------------------------------------------------------
// S9 (809) — Tax-Exempt Certificate.
// ---------------------------------------------------------------------------

/**
 * Tax-Exempt Certificate (SF 1094) — mirrors backend `GpcTaxExempt`.
 *
 * FULL official-form text fidelity (SF 1094, U.S. Tax Exemption Form). The
 * signature IMAGE fields are omitted; the typed name/title + date text fields
 * are kept. The deprecated `tax_exempt_no` / `certificate_provided` fields are
 * KEPT as backward-compatible aliases of the clarified canonical
 * `institution_tax_exempt_no` / `certificate_provided_to_vendor` (the backend
 * keeps both in sync).
 */
export interface GpcTaxExempt {
  // Institutional tax number (cardholder / purchaser)
  institution_tax_exempt_no: string;
  // Purchaser organization (cardholder / purchaser)
  department_agency: string;
  office: string;
  // Vendor / supplier (cardholder fills name; vendor acknowledges)
  vendor_name: string;
  vendor_address: string;
  // Articles / services (cardholder / purchaser)
  description: string;
  quantity: number | null;
  unit_price: number | null;
  total_amount: number | null;
  state_tax_excluded: number | null;
  local_tax_excluded: number | null;
  // Purchaser block (cardholder / purchaser) — signature omitted
  purchaser_name: string;
  purchaser_title: string;
  purchaser_address: string;
  certifying_date: string;
  // Finance / disbursing (AxOL voucher)
  voucher_no: string;
  designated_office_symbol: string;
  // Vendor acknowledgment (completed by the vendor)
  vendor_representative_name: string;
  vendor_representative_title: string;
  // Certificate status (cardholder / purchaser)
  certificate_provided_to_vendor: boolean;
  notes: string;
  // Deprecated aliases (BACKWARD COMPATIBLE) — synced with the canonical fields.
  tax_exempt_no: string;
  certificate_provided: boolean;
}

/** Map the backend snake_case tax-exempt dict into `GpcTaxExempt`. */
export function mapGpcTaxExempt(data: any): GpcTaxExempt {
  const safe = data ?? {};
  // The canonical institutional number / provided flag fall back to the
  // deprecated aliases so an older payload still hydrates correctly.
  const institutionNo =
    safe.institution_tax_exempt_no ?? safe.tax_exempt_no ?? '';
  const providedToVendor = !!(
    safe.certificate_provided_to_vendor ?? safe.certificate_provided
  );
  return {
    // Institutional tax number
    institution_tax_exempt_no: institutionNo,
    // Purchaser organization
    department_agency: safe.department_agency ?? '',
    office: safe.office ?? '',
    // Vendor / supplier
    vendor_name: safe.vendor_name ?? '',
    vendor_address: safe.vendor_address ?? '',
    // Articles / services
    description: safe.description ?? '',
    quantity: safe.quantity ?? null,
    unit_price: safe.unit_price ?? null,
    total_amount: safe.total_amount ?? null,
    state_tax_excluded: safe.state_tax_excluded ?? null,
    local_tax_excluded: safe.local_tax_excluded ?? null,
    // Purchaser block
    purchaser_name: safe.purchaser_name ?? '',
    purchaser_title: safe.purchaser_title ?? '',
    purchaser_address: safe.purchaser_address ?? '',
    certifying_date: safe.certifying_date ?? '',
    // Finance / disbursing
    voucher_no: safe.voucher_no ?? '',
    designated_office_symbol: safe.designated_office_symbol ?? '',
    // Vendor acknowledgment
    vendor_representative_name: safe.vendor_representative_name ?? '',
    vendor_representative_title: safe.vendor_representative_title ?? '',
    // Certificate status
    certificate_provided_to_vendor: providedToVendor,
    notes: safe.notes ?? '',
    // Deprecated aliases — mirror the canonical values.
    tax_exempt_no: institutionNo,
    certificate_provided: providedToVendor,
  };
}

/**
 * Persist the Tax-Exempt Certificate (`POST /api/gpc/forms/tax-exempt`, stage
 * 809). `tax_exempt_no` is seeded from the installation profile when absent.
 */
export async function saveGpcTaxExempt(
  flowId: string,
  tax: GpcTaxExempt,
  installationProfileId?: string,
  evidence?: Record<string, unknown>,
): Promise<GpcTaxExempt> {
  const response = await apiCall<any>('/api/gpc/forms/tax-exempt', 'POST', {
    flow_id: flowId,
    tax,
    ...(installationProfileId !== undefined
      ? { installation_profile_id: installationProfileId }
      : {}),
    ...(evidence && Object.keys(evidence).length ? { evidence } : {}),
  });
  if (!response.success) {
    throw buildContractError(response);
  }
  return mapGpcTaxExempt(response.data);
}

// ---------------------------------------------------------------------------
// S10 (810) — Receipt + Receiver Signature.
// ---------------------------------------------------------------------------

/** Receipt + receiver signature — mirrors backend `GpcReceipt`. */
export interface GpcReceipt {
  receipt_type: 'email_confirmation' | 'packing_list' | 'invoice' | 'other' | null;
  received_at: string | null;
  receiver_name: string;
  receiver_is_not_cardholder: boolean;
  notes: string;
  evidence?: Record<string, unknown>;
  illustrative_draft?: boolean;
  draft_description?: string;
}

/** Saved receipt plus the server-owned policy verdict for this stage. */
export interface GpcReceiptSaveResult {
  receipt: GpcReceipt;
  requiredFieldsValidation: {
    valid?: boolean;
    unresolved_fields?: Array<Record<string, unknown>>;
  } | null;
}

interface GpcReceiptApiResponse extends ApiResponse<any> {
  required_fields_validation?: GpcReceiptSaveResult['requiredFieldsValidation'];
}

/** Map the backend snake_case receipt dict into `GpcReceipt`. */
export function mapGpcReceipt(data: any): GpcReceipt {
  const safe = data ?? {};
  const evidence = safe.evidence && typeof safe.evidence === 'object'
    && !Array.isArray(safe.evidence)
    ? safe.evidence
    : undefined;
  return {
    receipt_type: safe.receipt_type ?? null,
    received_at: safe.received_at ?? null,
    receiver_name: safe.receiver_name ?? '',
    receiver_is_not_cardholder: !!safe.receiver_is_not_cardholder,
    notes: safe.notes ?? '',
    ...(evidence ? { evidence } : {}),
    ...(safe.illustrative_draft === true ? { illustrative_draft: true } : {}),
    ...(typeof safe.draft_description === 'string' ? { draft_description: safe.draft_description } : {}),
  };
}

/** Restore a receipt and its separately persisted evidence from stage output. */
export function restoreGpcReceiptFromStageOutput(
  output: any,
): GpcReceipt | null {
  if (!output?.receipt || typeof output.receipt !== 'object') return null;
  return mapGpcReceipt({
    ...output.receipt,
    evidence: output.evidence,
    illustrative_draft: output.illustrative_draft,
    draft_description: output.draft_description,
  });
}

/**
 * Persist the Receipt (`POST /api/gpc/forms/receipt`, stage 810). The request
 * carries `cardholder_name` so the route re-validates receiver ≠ cardholder
 * (400 on violation).
 */
export async function saveGpcReceipt(
  flowId: string,
  receipt: GpcReceipt,
  cardholderName: string,
  evidence?: Record<string, unknown>,
): Promise<GpcReceiptSaveResult> {
  const response = await apiCall<any>('/api/gpc/forms/receipt', 'POST', {
    flow_id: flowId,
    receipt,
    cardholder_name: cardholderName,
    ...(evidence && Object.keys(evidence).length ? { evidence } : {}),
  }) as GpcReceiptApiResponse;
  if (!response.success) {
    throw buildContractError(response);
  }
  return {
    receipt: mapGpcReceipt(response.data),
    requiredFieldsValidation:
      response.required_fields_validation &&
      typeof response.required_fields_validation === 'object'
        ? response.required_fields_validation
        : null,
  };
}

// ---------------------------------------------------------------------------
// S11 (811) — IUID / Disposition Tracker.
// ---------------------------------------------------------------------------

/** IUID / disposition-tracker record — mirrors backend `GpcIuid`. */
export interface GpcIuid {
  iuid: string;
  equipment_custodian: string;
  pilferable: boolean;
  disposition_note: string;
}

/** Map the backend snake_case IUID dict into `GpcIuid`. */
export function mapGpcIuid(data: any): GpcIuid {
  const safe = data ?? {};
  return {
    iuid: safe.iuid ?? '',
    equipment_custodian: safe.equipment_custodian ?? '',
    pilferable: !!safe.pilferable,
    disposition_note: safe.disposition_note ?? '',
  };
}

/** Persist the IUID record (`POST /api/gpc/forms/iuid`, stage 811). */
export async function saveGpcIuid(
  flowId: string,
  iuid: GpcIuid,
  evidence?: Record<string, unknown>,
): Promise<GpcIuid> {
  const response = await apiCall<any>('/api/gpc/forms/iuid', 'POST', {
    flow_id: flowId,
    iuid,
    ...(evidence && Object.keys(evidence).length ? { evidence } : {}),
  });
  if (!response.success) {
    throw buildContractError(response);
  }
  return mapGpcIuid(response.data);
}

// ===========================================================================
// GPC PER-FORM FILLABLE-PDF EXPORT (S4–S11).
//
// One client fn per backend `/pdf` endpoint (`backend/gpc_routes.py`). Each
// POSTs `{flow_id}`, reads the server-provided Content-Disposition filename,
// and triggers an anchor-click blob download — copying the Services COMBO
// `generateServicesComboPdf` pattern exactly. These use raw `fetch` (not
// `apiCall`) because the response body is a PDF blob, not the JSON envelope.
// ===========================================================================

const GPC_API_BASE_URL = process.env.REACT_APP_API_URL || '';

/** Resolve the session email header (mirrors the per-workflow modules). */
function gpcUserEmail(): string {
  return localStorage.getItem('userEmail') || (window as any).userEmail || '';
}

/**
 * Shared blob-download helper for the per-form PDF endpoints: POST `{flow_id}`,
 * read the Content-Disposition filename (falling back to `fallbackName`), and
 * anchor-click the blob. On a non-OK response the body is the JSON error
 * envelope — surface its `error` through a thrown `Error` carrying the contract
 * fields for analytics consumers.
 */
async function downloadGpcFormPdf(
  endpoint: string,
  flowId: string,
  fallbackName: string,
): Promise<void> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const email = gpcUserEmail();
  if (email) headers['X-User-Email'] = email;

  const response = await fetchWithChatFeedback(`${GPC_API_BASE_URL}${endpoint}`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ flow_id: flowId }),
    credentials: 'include',
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw buildContractError(body);
  }

  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : fallbackName;

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

/** Export the saved 889 representation (stage 804) as a fillable PDF. */
export async function generateGpc889Pdf(flowId: string): Promise<void> {
  return downloadGpcFormPdf('/api/gpc/forms/889/pdf', flowId, `GPC_889_${flowId}.pdf`);
}

/** Export the saved GPC Abstract (stage 805) as a fillable PDF. */
export async function generateGpcAbstractPdf(flowId: string): Promise<void> {
  return downloadGpcFormPdf('/api/gpc/abstract/pdf', flowId, `GPC_Abstract_${flowId}.pdf`);
}

/** Export the saved DD/DAF 2579 (stage 806) as a fillable PDF. */
export async function generateGpcDd2579Pdf(flowId: string): Promise<void> {
  return downloadGpcFormPdf('/api/gpc/forms/dd2579/pdf', flowId, `GPC_DD2579_${flowId}.pdf`);
}

/** Export the saved AF Form 9 (stage 807) as a fillable PDF. */
export async function generateGpcAfForm9Pdf(flowId: string): Promise<void> {
  return downloadGpcFormPdf('/api/gpc/forms/af9/pdf', flowId, `GPC_AF_Form_9_${flowId}.pdf`);
}

/** Export the saved Tax-Exempt Certificate (stage 809) as a fillable PDF. */
export async function generateGpcTaxExemptPdf(flowId: string): Promise<void> {
  return downloadGpcFormPdf('/api/gpc/forms/tax-exempt/pdf', flowId, `GPC_Tax_Exempt_${flowId}.pdf`);
}

/** Export the saved Receipt (stage 810) as a fillable PDF. */
export async function generateGpcReceiptPdf(flowId: string): Promise<void> {
  return downloadGpcFormPdf('/api/gpc/forms/receipt/pdf', flowId, `GPC_Receipt_${flowId}.pdf`);
}

/** Export the saved IUID / disposition record (stage 811) as a fillable PDF. */
export async function generateGpcIuidPdf(flowId: string): Promise<void> {
  return downloadGpcFormPdf('/api/gpc/forms/iuid/pdf', flowId, `GPC_IUID_${flowId}.pdf`);
}
